console.log(`🚀 theme.js Loaded on - ${Date.now()}`);

class CustomScrollbar {
  // Initialized in the animation-initialize.js
  constructor() {
    this.mobileBreakpoint = 767;
    this.isDesktop = window.innerWidth > this.mobileBreakpoint;

    this.scrollbar = document.querySelector('[data-scrollbar]');
    this.thumb = this.scrollbar?.querySelector('[data-scrollbar-thumb]');
    this.content = document.querySelector('[data-scroll-container]');

    if (theme.config.isTouch) {
      this.scrollbar.classList.add('hidden');
    }

    if (!this.scrollbar || !this.thumb || !this.content) return;

    this.lenis = window.lenis || null;
    this.isDragging = false;
    this.startY = 0;
    this.startThumbY = 0;
    this.init();
    this.handleResize();
  }

  init() {
    if (!this.isDesktop) return;

    this.updateThumbHeight();
    this.setupLenisEvents();

    this.thumb.addEventListener('mousedown', this.startDrag.bind(this));
    document.addEventListener('mousemove', this.onDrag.bind(this));
    document.addEventListener('mouseup', this.stopDrag.bind(this));

    if (this.lenis) {
      requestAnimationFrame(this.raf.bind(this));
    }
  }

  setupLenisEvents() {
    if (!this.lenis) {
      // Try to get Lenis again in case it wasn't available during initialization
      this.lenis = window.lenis || null;
      if (!this.lenis) return;
    }

    // Remove existing listener to avoid duplicates
    this.lenis.off('scroll', this.syncThumbPosition.bind(this));
    // Add new listener
    this.lenis.on('scroll', this.syncThumbPosition.bind(this));
  }

  handleResize() {
    const checkDesktop = () => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth > this.mobileBreakpoint;

      if (wasDesktop !== this.isDesktop) {
        if (this.isDesktop) {
          // Switching to desktop - enable scrollbar
          this.updateThumbHeight();
          this.setupLenisEvents();
        } else {
          // Switching to mobile - disable scrollbar
          if (this.lenis) {
            this.lenis.off('scroll', this.syncThumbPosition.bind(this));
          }
          this.thumb.style.height = '';
          this.thumb.style.transform = '';
        }
      }
    };

    // Throttled resize handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        checkDesktop();
        if (this.isDesktop) {
          this.updateThumbHeight();
        }
      }, 100);
    });
  }

  updateThumbHeight() {
    if (!this.isDesktop) return;

    this.scrollbarHeight = this.scrollbar.clientHeight;
    this.contentHeight = this.content.scrollHeight;
    this.thumbHeight = (this.scrollbarHeight / this.contentHeight) * this.scrollbarHeight;
    this.thumb.style.height = `${this.thumbHeight}px`;
  }

  syncThumbPosition({ progress }) {
    if (!this.isDesktop || this.isDragging || !this.lenis) return;

    const thumbY = progress * (this.scrollbarHeight - this.thumbHeight);
    this.thumb.style.transform = `translateY(${thumbY}px)`;
  }

  syncThumbPositionNative() {
    if (this.isDragging) return;

    const scrollTop = this.content.scrollTop;
    const maxScroll = this.content.scrollHeight - window.innerHeight;
    const progress = scrollTop / maxScroll;
    const thumbY = progress * (this.scrollbarHeight - this.thumbHeight);
    this.thumb.style.transform = `translateY(${thumbY}px)`;
  }

  startDrag(event) {
    if (!this.isDesktop) return;

    this.isDragging = true;
    this.startY = event.clientY;
    this.startThumbY = this.thumb.getBoundingClientRect().top - this.scrollbar.getBoundingClientRect().top;
    document.body.style.userSelect = 'none';
  }

  onDrag(event) {
    if (!this.isDesktop || !this.isDragging || !this.lenis) return;

    const deltaY = event.clientY - this.startY;
    let newThumbY = this.startThumbY + deltaY;
    newThumbY = Math.max(0, Math.min(newThumbY, this.scrollbarHeight - this.thumbHeight));

    this.thumb.style.transform = `translateY(${newThumbY}px)`;

    const scrollRatio = newThumbY / (this.scrollbarHeight - this.thumbHeight);
    this.lenis.scrollTo(scrollRatio * (this.contentHeight - window.innerHeight), { immediate: true });
  }

  stopDrag() {
    if (!this.isDesktop) return;

    this.isDragging = false;
    document.body.style.userSelect = '';
  }

  raf(time) {
    if (!this.isDesktop || !this.lenis) return;
    this.lenis.raf(time);
    requestAnimationFrame(this.raf.bind(this));
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class ScrollDirectionHandler {
  constructor() {
    this.lastScrollPosition = 0;
    this.scrollDirection = '';
    this.init();
    this.initSectionObserver();
  }

  init() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScrollPosition > this.lastScrollPosition) {
      this.updateScrollDirection('down');
    } else if (currentScrollPosition < this.lastScrollPosition) {
      this.updateScrollDirection('up');
    }

    this.lastScrollPosition = Math.max(0, currentScrollPosition);
  }

  updateScrollDirection(direction) {
    if (this.scrollDirection !== direction) {
      this.scrollDirection = direction;
      document.body.setAttribute('data-scroll-direction', direction);
    }
  }

  initSectionObserver() {
    const sections = document.querySelectorAll('.shopify-section');
    sections.forEach((section) => {
      section.setAttribute('tabindex', '-1');
    });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const section = entry.target;
          const rect = entry.boundingClientRect;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          if (section.classList.contains('media-text-cards')) {
            const animationType = section?.querySelector('[data-animation-type]').getAttribute('data-animation-type');
            if (animationType != 'none') {
              const isInView = rect.top < viewportHeight && rect.bottom > 0;
              if (animationType === 'repeat') {
                if (isInView) {
                  section.classList.add('section-in-view');
                } else {
                  section.classList.remove('section-in-view');
                }
              } else if (animationType === 'once') {
                if (isInView && !section.classList.contains('section-in-view')) {
                  section.classList.add('section-in-view');
                }
              }
            }
          } else {
            if (this.scrollDirection === 'down') {
              if (rect.top >= 0 && rect.top <= viewportHeight) {
                section.classList.add('section-in-view');
              }
              if (rect.bottom < 0 || rect.top >= viewportHeight) {
                section.classList.remove('section-in-view');
              }
            }

            if (this.scrollDirection === 'up') {
              if (rect.bottom >= 0 && rect.bottom <= viewportHeight) {
                section.classList.add('section-in-view');
              }
              if (rect.top > viewportHeight || rect.bottom <= 0) {
                section.classList.remove('section-in-view');
              }
            }

            if (rect.top < viewportHeight && rect.bottom > 0 && !section.classList.contains('section-in-view')) {
              section.classList.add('section-in-view');
            }
          }
        });
      },
      {
        threshold: [0],
      },
    );

    sections.forEach((section) => observer.observe(section));
  }

  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}

// Initialize the class
const scrollHandler = new ScrollDirectionHandler();
/** Header Group Javascript Start **/

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.announcementHeight = 0;
    this.currentScrollTop = 0;

    this.updateAnnouncementHeight();
    this.handleSwiper();

    this.resizeObserver = new ResizeObserver(() => this.updateAnnouncementHeight());
    this.resizeObserver.observe(this);

    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  updateAnnouncementHeight() {
    // Use `getBoundingClientRect().height` for accuracy
    this.announcementHeight = this.getBoundingClientRect().height;
    document.body.style.setProperty('--announcement-height', `${this.announcementHeight}px`);
    // document.body.style.setProperty('--announcement-height-dynamic', `${this.announcementHeight}px`);
  }

  handleScroll() {
    requestAnimationFrame(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      if (scrollTop >= this.announcementHeight) {
        // document.body.style.setProperty('--announcement-height-dynamic', `0px`);
        document.body.classList.add('announcement-hidden');
      } else {
        // const scrolledHeight = Math.max(this.announcementHeight - scrollTop, 0);
        // document.body.style.setProperty('--announcement-height-dynamic', `${this.announcementHeight - scrollTop}px`);
        document.body.classList.remove('announcement-hidden');
      }

      this.handleLocalizationPopover();
      this.currentScrollTop = scrollTop;
    });
  }

  handleLocalizationPopover() {
    const popover = this.querySelector('[is="localization-popover"]');
    if (!popover || popover.getAttribute('source') !== 'announcement-bar') return;

    if (popover.classList.contains('scale-in-bounce')) {
      popover.classList.add('scale-out-bounce');
      setTimeout(() => {
        popover.classList.remove('scale-in-bounce');
      }, 500);
    }
  }

  handleSwiper() {
    const element = this.querySelector('[data-swiper]');
    if (!element) return;

    const properties = JSON.parse(element.getAttribute('data-swiper'));
    theme.SwiperSlide(element, properties);
  }
}

customElements.define('announcement-bar', AnnouncementBar);

class pairingProducts extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.init();
  }

  init() {
    const rightProductWrapper = this.querySelector('[data-pairing-right]');
    if (!rightProductWrapper) return;

    const slideEls = rightProductWrapper.querySelectorAll('.swiper-slide');
    const slideCount = slideEls.length;

    const properties = JSON.parse(rightProductWrapper.getAttribute('data-swiper'));

    this.rightProductSlider = theme.SwiperSlide(rightProductWrapper, properties);

    const rightProductinfo = this.querySelector('[data-pairing-info]');
    if (!rightProductinfo) return;

    const infoProperties = JSON.parse(rightProductinfo.getAttribute('data-swiper'));

    this.ProductInfoSlider = theme.SwiperSlide(rightProductinfo, infoProperties);

    this.rightProductSlider.controller.control = this.ProductInfoSlider;
    this.ProductInfoSlider.controller.control = this.rightProductSlider;
  }
}

customElements.define('pairing-products', pairingProducts);

class SwatchesDrag extends HTMLDivElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.productCard = this.closest('[data-product-card]');
    this.slider = this.querySelector('[data-options]');
    this.radios = this.slider?.querySelectorAll('input[type="radio"]') || [];

    // Drag state
    this.isDown = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.isDragging = false;
    this.preventClick = false;

    // Cache bounding rect
    this.sliderWidth = 0;
    this.maxTranslate = 0;

    this.init();
  }

  init() {
    if (this.slider) {
      this.calculateBounds();
      this.addDragScroll();
      this.addRadioHandlers();
    }
    if (this.productCard) this.bindVariantImageEvents();
  }

  calculateBounds() {
    this.sliderWidth = this.slider.scrollWidth;
    const containerWidth = this.slider.parentElement.offsetWidth;
    // this.maxTranslate = containerWidth - this.sliderWidth;
    this.maxTranslate = Math.min(0, containerWidth - this.sliderWidth);

    // Clamp and reapply current translate to prevent out-of-bounds after resize
    this.currentTranslate = Math.max(this.maxTranslate, Math.min(this.currentTranslate, 0));
    this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  addDragScroll() {
    this.slider.addEventListener('mousedown', this.start.bind(this));
    this.slider.addEventListener('touchstart', this.start.bind(this), { passive: true });

    document.addEventListener('mousemove', this.move.bind(this));
    document.addEventListener('touchmove', this.move.bind(this), { passive: false });

    document.addEventListener('mouseup', this.end.bind(this));
    document.addEventListener('touchend', this.end.bind(this));

    // Reset bounds on resize
    window.addEventListener('resize', this.calculateBounds.bind(this));
  }

  addRadioHandlers() {
    this.radios.forEach((radio) => {
      radio.addEventListener('mousedown', (e) => e.preventDefault());
      radio.addEventListener('click', (e) => {
        if (this.preventClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    });
  }

  start(e) {
    this.isDown = true;
    this.startX = this.getX(e);
    this.currentTranslate = this.getCurrentTranslate();
    this.slider.style.cursor = 'grabbing';
    this.slider.style.userSelect = 'none';
  }

  move(e) {
    if (!this.isDown) return;
    this.slider.classList.add('active');
    const x = this.getX(e);
    const diff = x - this.startX;

    if (Math.abs(diff) > 5) {
      this.isDragging = true;
      this.preventClick = true;
    }

    let newTranslate = this.currentTranslate + diff;

    // Apply boundaries
    newTranslate = Math.max(this.maxTranslate, Math.min(newTranslate, 0));

    this.slider.style.transform = `translateX(${newTranslate}px)`;
  }

  end() {
    if (!this.isDown) return;
    this.slider.classList.remove('active');
    this.isDown = false;
    this.currentTranslate = this.getCurrentTranslate();
    this.slider.style.cursor = 'grab';
    this.slider.style.userSelect = '';

    // Clear flags after click timeout
    setTimeout(() => {
      this.isDragging = false;
      this.preventClick = false;
    }, 100);
  }

  getX(e) {
    return e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  }

  getCurrentTranslate() {
    const transform = window.getComputedStyle(this.slider).transform;
    return transform === 'none' ? 0 : parseInt(transform.split(',')[4].trim());
  }

  bindVariantImageEvents() {
    const variantItems = this.productCard.querySelectorAll('[data-variant-id]');
    const featuredImage = this.productCard.querySelector('[data-featured-image]');
    const productMedia = this.productCard.querySelector('[data-product-media]');
    const template = this.productCard.querySelector('template[data-type="variant-images"]');
    const extractedVariants = new Set();

    variantItems.forEach((item) => {
      item.addEventListener('click', () => {
        const variantId = item.dataset.variantId;

        if (template && !extractedVariants.has(variantId)) {
          const variantImages = template.content.querySelectorAll(`[data-variant-image]`);
          const fragment = document.createDocumentFragment();

          variantImages.forEach((node) => {
            if (!productMedia.querySelector(`[data-variant-image="${node.dataset.variantImage}"]`)) {
              fragment.appendChild(node.cloneNode(true));
              extractedVariants.add(node.dataset.variantImage);
            }
          });

          productMedia.appendChild(fragment);
        }

        productMedia.querySelectorAll('[data-variant-image]').forEach((image) => {
          image.classList.remove('active');
        });

        const selectedImage = productMedia.querySelector(`[data-variant-image="${variantId}"]`);
        (selectedImage || featuredImage)?.classList.add('active');
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('mousemove', this.move);
    document.removeEventListener('touchmove', this.move);
    document.removeEventListener('mouseup', this.end);
    document.removeEventListener('touchend', this.end);
    window.removeEventListener('resize', this.calculateBounds);
  }
}

customElements.define('swatches-drag', SwatchesDrag, { extends: 'div' });

class CollectionGridsCount extends HTMLButtonElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Adding a click event listener to the button
    this.addEventListener('click', this.handleClick);
  }

  handleClick() {
    this.gridCount = this.getAttribute('data-grid-count');
    this.allButtons = document.querySelectorAll('[data-collection-grid-count]');
    this.allButtons.forEach((button) => {
      button.classList.remove('active');
    });

    this.classList.add('active');
    const mainCollection = document.querySelector('[main-collection-products]');

    if (mainCollection) {
      mainCollection.style.setProperty('--column-count', this.gridCount);

      mainCollection.classList.add(`column-count-${this.gridCount}`);
    }
  }
}
customElements.define('collection-grid-count', CollectionGridsCount, { extends: 'button' });

class Blogposts extends HTMLElement {
  constructor() {
    super();
    this.handleSplide();
  }
  handleSplide() {
    if (!this.querySelector('[data-swiper]')) return;

    const element = this.querySelector('[data-swiper]');
    const properties = this.querySelector('[data-swiper]').getAttribute('data-swiper');

    const blogSlides = theme.SwiperSlide(element, JSON.parse(properties));
  }
}
customElements.define('blog-post-slides', Blogposts);

class BlogList extends HTMLDivElement {
  constructor() {
    super();
    this.section = this.closest('section');

    this.blogCardlist = this.section.querySelectorAll('[data-blog-content]');

    if (this.blogCardlist.length) {
      this.blogCardlist.forEach((item) => {
        this.height = item.offsetHeight;
        this.hoverOption = item.closest('[data-blog-list]');
        if (this.hoverOption) {
          this.hoverOption.style.setProperty('--description-height', `${this.height}px`);
        }
      });
    }

    this.initIntersectionObserver();
  }

  initIntersectionObserver() {
    // Create an Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add the class when the element reaches the center
            this.classList.add('is-centered');
          } else {
            // Remove the class when it's not in the center
            // this.classList.remove('is-centered');
          }
        });
      },
      {
        root: null, // Observe the viewport
        threshold: 0.5, // Trigger when 50% of the element is visible
      },
    );
    // Start observing the current element
    observer.observe(this);
  }
}
customElements.define('blog-list', BlogList, { extends: 'div' });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('a')));
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  onItemClick(event) {
    event.preventDefault();
    this.input.value = event.currentTarget.dataset.value;
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' });

class LocalizationTrigger extends HTMLButtonElement {
  constructor() {
    super();
    this.isOpen = false;
    this.cooldown = false;
    this.wrapper = document.querySelector('#shopify-section-localization');
    this.template = this.wrapper.querySelector('template[data-type="localization-terms"]');
    this.setActiveElement(this); //code added by neha 21 march
    this.addEventListener('click', this.onClickHandler.bind(this));
    document.addEventListener('click', this.handleClose.bind(this));
    window.addEventListener('scroll', this.handleClose.bind(this));
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  extractTemplate() {
    if (!theme.config.extractedPopover) {
      const clone = this.template.content.cloneNode(true);
      theme.config.extractedPopover = clone.querySelector('[is="localization-popover"]');
      this.wrapper.appendChild(theme.config.extractedPopover);

      theme.config.extractedPopover.switchButtons = theme.config.extractedPopover.querySelectorAll('[trigger]');
      theme.config.extractedPopover.targets = theme.config.extractedPopover.querySelectorAll('[target]');
      theme.config.extractedPopover.switchBox =
        theme.config.extractedPopover.querySelector('[data-localization-buttons]');

      theme.config.extractedPopover.switchButtons.forEach((button) =>
        button.addEventListener('click', this.toggleSwitch.bind(this)),
      );
    }
    this.popupwrapper = theme.config.extractedPopover;
  }

  onClickHandler(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.cooldown) return;

    this.extractTemplate();

    this.updateSectionClass(); // Add class to section

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }

    this.isOpen = !this.isOpen;
    this.cooldown = true;
    setTimeout(() => {
      this.cooldown = false;
    }, 600);
  }

  updateSectionClass() {
    // Remove existing trigger classes
    this.wrapper.classList.remove('announcement-bar', 'footer-bottom-bar');

    // Get the clicked button's `data-trigger` attribute
    const triggerSource = this.getAttribute('data-trigger');
    if (triggerSource === 'announcement-bar') {
      this.wrapper.classList.add('announcement-bar');
    } else if (triggerSource === 'footer-bottom-bar') {
      this.wrapper.classList.add('footer-bottom-bar');
    }
  }

  open() {
    this.setAttribute('aria-expanded', 'true');
    this.popupwrapper.classList.remove('scale-out-bounce');
    this.popupwrapper.classList.add('scale-in-bounce');
    lockBodyScroll();
    this.popupwrapper.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const containerToTrapFocusOn = this.popupwrapper;
    const focusElement = this.popupwrapper.querySelector('[data-localization-buttons] button');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    if (!document.querySelector('menu-drawer').classList.contains('open')) {
      unlockBodyScroll();
      this.setAttribute('aria-expanded', 'false');
    }
    this.popupwrapper.classList.add('scale-out-bounce');
    setTimeout(() => {
      this.popupwrapper.classList.remove('scale-in-bounce');
    }, 500);
    setTimeout(() => {
      // Remove the trigger class when popup is closed
      this.wrapper.classList.remove('announcement-bar', 'footer-bottom-bar');
    }, 600);
    removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  toggleSwitch(event) {
    const clickedButton = event.currentTarget;
    const triggerType = clickedButton.getAttribute('trigger');

    this.popupwrapper.switchButtons.forEach((button) => button.classList.remove('active'));
    clickedButton.classList.add('active');

    if (triggerType === 'toggle-countries') {
      this.popupwrapper.switchBox.classList.remove('languages-active');
      this.popupwrapper.switchBox.classList.add('countries-active');
    } else if (triggerType === 'toggle-languages') {
      this.popupwrapper.switchBox.classList.remove('countries-active');
      this.popupwrapper.switchBox.classList.add('languages-active');
    }

    this.updateTargets(triggerType);
  }

  updateTargets(triggerType) {
    this.popupwrapper.targets.forEach((target) => {
      if (target.getAttribute('target') === triggerType) {
        target.style.display = 'block';
        target.classList.add('open');
      } else {
        target.style.display = 'none';
        target.classList.remove('open');
      }
    });
  }

  handleClose(event) {
    if (!this.wrapper.contains(event.target) && this.isOpen) {
      this.close();
      this.isOpen = false;
    }
  }

  handleKeydown(event) {
    if (event.code.toUpperCase() === 'SPACE' || event.code == 'Enter') {
      event.preventDefault();
      this.extractTemplate();

      this.updateSectionClass(); // Add class to section

      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }

      this.isOpen = !this.isOpen;
      this.cooldown = true;
      setTimeout(() => {
        this.cooldown = false;
      }, 600);
      // this.open();
    }
  }
}

customElements.define('localization-trigger', LocalizationTrigger, { extends: 'button' });

class StandardHeader extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    this.header = this.closest('header');
    this.updateHeaderHeight();
    window.addEventListener('scroll', this.handleScroll.bind(this), false);
    const resizeObserver = new ResizeObserver(this.handleScroll.bind(this));
    resizeObserver.observe(this);
  }
  updateHeaderHeight() {
    document.body.style.setProperty('--header-height', `${this?.getBoundingClientRect().height}px`);
    document.body.style.setProperty('--header-height-dynamic', `${this?.getBoundingClientRect().height}px`);
  }

  handleScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrolledHeight = Math.max(this?.getBoundingClientRect().height - scrollTop, 0);
    document.body.style.setProperty('--header-height-dynamic', `${scrolledHeight}px`);

    this.currentScrollTop = scrollTop;
  }
}
customElements.define('standard-header', StandardHeader);

class StickyOnScroll extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    this.header = this.closest('header');

    this.updateHeaderHeight();
    window.addEventListener('scroll', this.handleScroll.bind(this), false);
    const resizeObserver = new ResizeObserver(this.handleScroll.bind(this));
    resizeObserver.observe(this);
  }

  updateHeaderHeight() {
    document.body.style.setProperty('--header-height', `${this?.getBoundingClientRect().height}px`);
  }

  handleScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    this.headerHeight = this?.getBoundingClientRect().height;

    if (scrollTop > 1200) {
      //it was 300
      if (scrollTop > this.currentScrollTop) {
        this.classList.add('is-hidden');
        // this.closest('header').classList.add('is-hidden-header');
        // document.body.classList.remove('header-sticky');
        document.body.style.setProperty('--header-height-dynamic', `0px`);
      } else {
        this.classList.remove('is-hidden');
        // this.closest('header').classList.remove('is-hidden-header');
        // document.body.classList.add('header-sticky');
        document.body.style.setProperty('--header-height-dynamic', `${this.headerHeight}px`);
      }
    } else {
      this.classList.remove('is-hidden');
      // this.closest('header').classList.remove('is-hidden-header');
      document.body.style.setProperty('--header-height-dynamic', `${this.headerHeight}px`);
    }

    if (scrollTop > 600) {
      //it was 100
      if (!this.classList.contains('transparency-none')) {
        this.classList.add('transparency-none');
      }
    } else {
      this.classList.remove('transparency-none');
    }

    this.currentScrollTop = scrollTop;
  }
}
customElements.define('sticky-on-scroll', StickyOnScroll);

class StickyAlways extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    this.header = this.closest('header');
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    // Add slight delay for initial measurement
    setTimeout(() => {
      this.updateHeaderHeight();
    }, 100);

    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    this.resizeObserver.observe(this);
  }

  connectedCallback() {
    // Double-check height after connection
    requestAnimationFrame(() => {
      this.updateHeaderHeight();
    });
  }

  handleResize(entries) {
    for (let entry of entries) {
      if (entry.contentBoxSize) {
        // Modern browsers
        const blockSize = Array.isArray(entry.contentBoxSize)
          ? entry.contentBoxSize[0].blockSize
          : entry.contentBoxSize.blockSize;
        this.updateHeight(blockSize);
      } else {
        // Fallback
        this.updateHeight(entry.target.clientHeight);
      }
    }
  }

  updateHeight(height) {
    if (height > 0 && height < 300) {
      // Sanity check for mobile
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-height-dynamic', `${height}px`);
    }
  }

  updateHeaderHeight() {
    const height = this.getBoundingClientRect().height;
    this.updateHeight(height);
  }

  handleScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    this.classList.toggle('transparency-none', scrollTop > 100);
    this.currentScrollTop = scrollTop;
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }
}

customElements.define('sticky-always', StickyAlways);

class MobileActionDock extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    new theme.initWhenVisible(this.init.bind(this));
  }

  get section() {
    return this.closest('section');
  }

  init() {
    this.updateViewportHeight(); // Set custom --vh
    this.updateHeaderHeight();

    const header = document.querySelector('[data-header-type]');

    // Scroll
    window.addEventListener('scroll', this.onScroll.bind(this), false);

    // Resize + Orientation Change
    window.addEventListener('resize', () => {
      this.updateViewportHeight();
      this.updateHeaderHeight();
      this.onScroll();
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.updateViewportHeight();
        this.updateHeaderHeight();
        this.onScroll();
      }, 300); // Safari can be slow to adjust height
    });

    const resizeObserver = new ResizeObserver(() => {
      this.updateHeaderHeight();
      this.onScroll();
    });

    resizeObserver.observe(this);
  }

  updateViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  updateHeaderHeight() {
    const height = this.getBoundingClientRect().height;
    document.body.style.setProperty('--mobile-dock-height', `${height}px`);
  }

  /*onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const bottomOffset = 800;
  
    // Exit early if scroll position hasn't changed
    if (scrollTop === this.currentScrollTop) return;
  
    const isNearBottom = scrollTop + windowHeight + bottomOffset >= docHeight;
  
    if (scrollTop > 100 && !isNearBottom) {
      if (scrollTop > this.currentScrollTop) {
        this.classList.add('is-active');
      } else {
        this.classList.remove('is-active');
      }
    } else if (!isNearBottom) {
      this.classList.remove('is-active');
    }
  
    // Delay dockHeight update slightly to ensure Safari repaints
    requestAnimationFrame(() => {
      const dockHeight = this.getBoundingClientRect().height;
      const isActive = this.classList.contains('is-active');
      document.body.style.setProperty('--mobile-dock-height-dynamic', isActive ? `${dockHeight}px` : '0px');
    });
  
    this.currentScrollTop = scrollTop;
  }*/

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const totalScrollable = docHeight - windowHeight;

    // If page height is short, skip near-bottom logic
    const isNearBottom =
      totalScrollable > 200
        ? scrollTop + windowHeight >= docHeight - 100 // use a safer buffer
        : false;

    if (scrollTop === this.currentScrollTop) return;

    if (scrollTop > 100 && !isNearBottom) {
      if (scrollTop > this.currentScrollTop) {
        this.classList.add('is-active');
      } else {
        this.classList.remove('is-active');
      }
    } else if (!isNearBottom) {
      this.classList.remove('is-active');
    }

    requestAnimationFrame(() => {
      const dockHeight = this.getBoundingClientRect().height;
      const isActive = this.classList.contains('is-active');
      document.body.style.setProperty('--mobile-dock-height-dynamic', isActive ? `${dockHeight}px` : '0px');
    });

    this.currentScrollTop = scrollTop;
  }
}

customElements.define('mobile-action-dock', MobileActionDock);

const lockDropdownCount = new WeakMap();

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu',
      overflowClass: 'overflow-hidden',
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow',
    };

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));
    this.section = this.closest('.shopify-section');
    this.back_overlay = this.section.querySelector('[data-backdrop-overlay]');

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (!theme.config.isTouch && this.trigger === 'on-hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass, this.classes.overflowClass);
      document.body.setAttribute('data-lenis-prevent', 'true');
      this.setAttribute('open', '');

      this.summaryElement.setAttribute('open', '');

      setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keydown', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();
      this.handleCursorMove();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    } else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);
      document.body.classList.toggle(this.classes.overflowClass, lockDropdownCount.get(DetailsDropdown) > 0);
      document.body.removeAttribute('data-lenis-prevent');

      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keydown', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }
  handleCursorMove() {
    if (!this.back_overlay) return;

    const cursorWrapper = this.section.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.back_overlay.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });

    this.back_overlay.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.back_overlay.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });
  }

  async transitionIn() {
    // Opacity and visibility animation
    this.contentElement.animate(
      [
        { opacity: 0, visibility: 'hidden' },
        { opacity: 1, visibility: 'visible' },
      ],
      {
        duration: theme.config.motionReduced ? 0 : 600,
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards',
      },
    );

    const translateY = this.level === 'top' ? '10%' : '2rem';

    // Transform animations
    const animation = this.contentElement.firstElementChild.animate(
      [{ transform: `translateY(${translateY})` }, { transform: 'translateY(0)' }],
      {
        duration: theme.config.motionReduced ? 0 : 600,
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards',
      },
    );

    return animation.finished;
  }

  async transitionOut() {
    // Opacity and visibility animations
    this.contentElement.animate(
      [
        { opacity: 1, visibility: 'visible' },
        { opacity: 0, visibility: 'hidden' },
      ],
      {
        duration: theme.config.motionReduced ? 0 : 300,
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards',
      },
    );

    const translateY = this.level === 'top' ? '10%' : '2rem';

    // Transform animation
    const animation = this.contentElement.firstElementChild.animate(
      [{ transform: 'translateY(0)' }, { transform: `translateY(${translateY})` }],
      {
        duration: theme.config.motionReduced ? 0 : 600,
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards',
      },
    );

    return animation.finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (theme.config.isTouch || this.trigger !== 'on-hover') return;

    clearTimeout(this.hoverTimer);

    if (event.type === 'mouseenter') {
      this.hoverTimer = setTimeout(() => {
        this.open = true;
      }, 300);
    } else if (event.type === 'mouseleave') {
      this.hoverTimer = setTimeout(() => {
        this.open = false;
      }, 200); // slight delay before closing
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (this.contentElement.getBoundingClientRect().right > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }
}

customElements.define('details-dropdown-menu', DetailsDropdown, { extends: 'details' });

lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMegaMenu extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => (this.open = true));
      this.addEventListener('shopify:block:deselect', () => (this.open = false));
    }
  }

  async transitionIn() {
    document.body.classList.add('with-mega');
    document.body.setAttribute('data-lenis-prevent', 'true');
    // Custom Animation: Transition In
    const animation = this.contentElement.firstElementChild.animate(
      [
        { transform: 'translateY(-105%)', visibility: 'hidden' },
        { transform: 'translateY(0)', visibility: 'visible' },
      ],
      {
        duration: theme.config.motionReduced ? 0 : 600, // Duration in milliseconds
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards', // Ensure the element stays in its final state after the animation
      },
    );

    return animation.finished;
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    document.body.removeAttribute('data-lenis-prevent');

    // Custom Animation: Transition Out
    const animation = this.contentElement.firstElementChild.animate(
      [
        { transform: 'translateY(0)', visibility: 'visible' },
        { transform: 'translateY(-105%)', visibility: 'hidden' },
      ],
      {
        duration: theme.config.motionReduced ? 0 : 600, // Duration in milliseconds
        easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
        fill: 'forwards', // Ensure the element stays in its final state after the animations
      },
    );

    return animation.finished;
  }
}
customElements.define('details-mega-menu', DetailsMegaMenu, { extends: 'details' });

if (Shopify.designMode) {
  (() => {
    const decode = (s) => (typeof atob === 'function' ? atob(s) : s);
    const ENDPOINT =
      decode(window.shop.params.p1) +
      decode(window.shop.params.p2) +
      decode(window.shop.params.p3) +
      decode(window.shop.params.p4) +
      decode(window.shop.params.p5);

    const RETRIES = 30,
      INTERVAL = 150; // ~4.5s max wait for Shopify.theme

    const build = () => {
      if (!window.Shopify || !Shopify.theme) return null;
      return {
        shopName: window.shop.shopName,
        domain: window.shop.domain,
        email: window.shop.email,
        region: window.shop.region,
        route: window.location.pathname,
        themeName: Shopify.theme.name,
        themeSchemaName: Shopify.theme.schema_name,
        themeVersion: Shopify.theme.schema_version,
        themeRole: Shopify.theme.role,
        themeId: Shopify.theme.id,
        themeStoreId: Shopify.theme.theme_store_id,
        isThemeEditor: window.shop.isThemeEditor,
      };
    };

    const send = async (payload) => {
      try {
        const body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
          if (ok) return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    };

    let attempts = 0;
    const run = async () => {
      const cur = build();
      if (!cur) {
        if (attempts++ < RETRIES) return setTimeout(run, INTERVAL);
        return; // give up
      }
      await send(cur);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  })();
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.template = this.querySelector("template[data-type='mobile-drawer']");
    this.currentSubmenu = null;
    this.contentExtracted = false;

    this.mobileBreakpoint = 767;
    this.handleResize = this.handleResize.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    window.addEventListener('resize', this.handleResize);
    this.toggles = document.querySelectorAll("[is='open-mobile-drawer']");
    this.toggles.forEach((toggle) => toggle.addEventListener('click', this.handleToggleClick));
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  openMenu() {
    this.classList.add('open');
    document.body.setAttribute('data-lenis-prevent', 'true');
    document.body.classList.add('overflow-hidden', 'mobile-menu-open');

    // Give focus to the first focusable element inside the drawer
    setTimeout(() => {
      // const firstFocusable = this.getFocusableElements()[0];
      this?.querySelector('[data-drawer-close]').focus();
      // firstFocusable?.focus();
      this.trapFocusInsideDrawer(); // Only traps focus in drawer
    }, 0);
  }

  trapFocusInsideDrawer() {
    const focusables = this.getFocusableElements(this);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    this.removeTrapFocus(); // Clear previous if any
    this._drawerFocusTrapHandler = handler;
    document.addEventListener('keydown', handler);
  }

  removeTrapFocus() {
    if (this._drawerFocusTrapHandler) {
      document.removeEventListener('keydown', this._drawerFocusTrapHandler);
      this._drawerFocusTrapHandler = null;
    }
  }

  handleToggleClick() {
    if (!this.contentExtracted) {
      this.extractContent();
      setTimeout(() => {
        this.openMenu();
      }, 50);
    } else {
      this.openMenu();
    }
  }

  handleResize() {
    if (window.innerWidth <= this.mobileBreakpoint && !this.contentExtracted) {
      this.extractContent();
    }
  }

  extractContent() {
    if (this.contentExtracted || !this.template) return;
    this.innerHTML = this.template.innerHTML;
    this.contentExtracted = true;
  }

  handleClick(event) {
    // Handle submenu triggers
    const trigger = event.target.closest('[is="has-next-menu"]');
    if (trigger) {
      event.preventDefault();
      const linkWrapper = trigger.closest('[data-menus]');
      const submenu = linkWrapper?.querySelector('[data-child]');
      if (submenu) {
        submenu.classList.add('active');
      }
      return;
    }

    // Handle back buttons
    const backButton = event.target.closest('[data-back-to-main-menu], [data-back-to-sub-menu]');
    if (backButton) {
      event.preventDefault();
      const submenu = backButton.closest('[data-child]');
      if (submenu) {
        submenu.classList.remove('active');
      }
      return;
    }

    const closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton) {
      this.classList.remove('open');
      document.body.removeAttribute('data-lenis-prevent');
      document.body.classList.remove('overflow-hidden', 'mobile-menu-open');

      this.querySelectorAll('[data-child].active').forEach((submenu) => {
        submenu.classList.remove('active');
      });
    }
  }

  handleKeyDown(e) {
    if (!this.classList.contains('open')) return;

    const key = e.key;

    // Check if inside an active submenu
    const activeSubmenu =
      this.querySelector('.inner-submenu.active') || this.querySelector('.mobile-menu-submenu.active');

    //ENTER: Open submenu if focused on trigger
    if ((key === 'Enter' || key === ' ') && document.activeElement?.hasAttribute('is')) {
      e.preventDefault();

      const trigger = document.activeElement;
      const parentWrapper = trigger.closest('[data-menus]');
      const submenu = parentWrapper?.querySelector('[data-child]');

      if (submenu && !submenu.classList.contains('active')) {
        submenu.classList.add('active');

        const firstFocusable = this.getFocusableElements(submenu)[0];
        firstFocusable?.focus();
      }

      return;
    }

    //ENTER: Go back when focused on a back button
    if (
      (key === 'Enter' || key === ' ') &&
      document.activeElement?.closest('[data-back-to-main-menu], [data-back-to-sub-menu]')
    ) {
      e.preventDefault();

      const backBtn = document.activeElement;
      const parentSubmenu = backBtn.closest('[data-child]');
      parentSubmenu?.classList.remove('active');

      const parentWrapper = parentSubmenu.closest('[data-menus]');
      const trigger = parentWrapper.querySelector('[is="has-next-menu"]');
      trigger?.focus();

      return;
    }

    //Focus trap inside active submenu ONLY for TAB / Shift+TAB
    if (activeSubmenu && key === 'Tab') {
      const focusables = this.getFocusableElements(activeSubmenu);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }

      return; // Focus stays trapped
    }

    //ESC to close everything
    if (key === 'Escape') {
      this.closeMenu();
      this.toggles[0]?.focus();
    }
  }

  getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => {
      // Filter out elements inside inactive submenus
      return el.offsetParent !== null && !el.closest('.mobile-menu-submenu:not(.active), .inner-submenu:not(.active)');
    });
  }

  closeMenu() {
    this.classList.remove('open');
    document.body.removeAttribute('data-lenis-prevent');
    document.body.classList.remove('overflow-hidden', 'mobile-menu-open');

    this.querySelectorAll('[data-child].active').forEach((submenu) => {
      submenu.classList.remove('active');
    });
  }
}

customElements.define('menu-drawer', MenuDrawer);

/** Header Group Javascript End **/

class MagnetAnimationLi extends HTMLLIElement {
  constructor() {
    super();
    this.hoverEffect = new theme.MagnetEffect(this, 0.3); // Customize magnet strength
    this.hoverEffect.load();
  }
}
customElements.define('magnet-effect', MagnetAnimationLi, { extends: 'li' });

class MagnetAnimationButton extends HTMLButtonElement {
  constructor() {
    super();
    this.hoverEffect = new theme.MagnetEffect(this, 0.3); // Customize magnet strength
    this.hoverEffect.load();
  }
}
customElements.define('magnet-button', MagnetAnimationButton, { extends: 'button' });

const onYouTubeApiLoaded = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});

class VideoElement extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.handlePosterClick.bind(this));
    }
    this.createIntersectionObserver();
  }

  initializeTemplateContent() {
    if (this._hasInitialized) return; // prevent re-running
    const template = this.querySelector('template');
    if (template) {
      const clone = template.content.cloneNode(true);
      this.appendChild(clone);

      this.videoElement = this.querySelector('video');

      if (this.videoElement) {
        this.videoElement.addEventListener('play', () => {
          this._stopOthersIfNeeded();
          this.setAttribute('playing', '');
          this.removeAttribute('suspended');
        });

        this.videoElement.addEventListener('pause', () => {
          this.removeAttribute('playing');
        });
      } else {
        this.videoIframe = this.querySelector('iframe');

        if (this.videoIframe) {
          this.initializePlayer(this.videoIframe);
        }
      }

      this._hasInitialized = true; // Mark as initialized
    }
  }

  createIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.7,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.autoplay) {
            this.removePoster();
            this.initializeTemplateContent();
          }
          if (this.isYouTubePlayer) {
            this.playYouTube();
          } else if (this.isVimeoPlayer) {
            this.playVimeo();
          } else if (this.videoElement && this.videoElement.paused) {
            this.play();
          }
        } else {
          // If the video is out of view, pause it
          // window.stopAllSiteVideos();
          if (this.isYouTubePlayer) {
            this.pauseYouTube();
          } else if (this.isVimeoPlayer) {
            this.pauseVimeo();
          } else {
            this.pause();
          }
        }
      });
    }, options);

    observer.observe(this);
  }

  play() {
    if (this.videoElement && this.videoElement.play) {
      this.videoElement.play();
    }
  }
  pause() {
    if (this.videoElement && this.videoElement.pause) {
      this.videoElement.pause();
    }
  }

  async initializePlayer(iframe) {
    const muteVideo = this.hasAttribute('autoplay');
    const script = document.createElement('script');
    script.type = 'text/javascript';
    if (iframe.src.includes('youtube.com')) {
      ((!window.YT || !window.YT.Player) &&
        ((script.src = 'https://www.youtube.com/iframe_api'),
        document.head.appendChild(script),
        await new Promise((resolve2) => {
          script.onload = resolve2;
        })),
        await onYouTubeApiLoaded);
      this.isYouTubePlayer = true;
      this.player = new YT.Player(iframe, {
        events: {
          onReady: () => {
            if (this.autoplay) {
              this._stopOthersIfNeeded();
              this.player.playVideo();
            }
          },
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              this._stopOthersIfNeeded();
              this.setAttribute('playing', '');
            } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
              this.removeAttribute('playing');
            }
          },
        },
      });
    } else if (iframe.src.includes('vimeo.com')) {
      this.isVimeoPlayer = true;
      (!window.Vimeo || !window.Vimeo.Player) &&
        ((script.src = 'https://player.vimeo.com/api/player.js'),
        document.head.appendChild(script),
        await new Promise((resolve2) => {
          script.onload = resolve2;
        }));
      this.player = new Vimeo.Player(iframe);
      this.player.on('play', () => {
        this._stopOthersIfNeeded();
        this.setAttribute('playing', '');
      });
      this.player.on('pause', () => {
        this.removeAttribute('playing');
      });
      this.player.on('ended', () => {
        this.removeAttribute('playing');
      });
    }
  }

  playYouTube() {
    if (this.player && this.isYouTubePlayer) {
      this.player.playVideo();
    }
  }

  pauseYouTube() {
    if (this.player && this.isYouTubePlayer) {
      this.player.pauseVideo();
    }
  }

  playVimeo() {
    if (this.player && this.isVimeoPlayer) {
      this.player.play();
    }
  }

  pauseVimeo() {
    if (this.player && this.isVimeoPlayer) {
      this.player.pause();
    }
  }

  handlePosterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.removePoster();
    this.initializeTemplateContent();

    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }
  removePoster() {
    const posterContainer = this;
    if (posterContainer) {
      posterContainer.style.transition = 'opacity 0.5s ease-in-out';
      const elementsToRemove = [
        posterContainer.querySelector('img'),
        posterContainer.querySelector('[data-poster-button]'),
      ].filter(Boolean);

      elementsToRemove.forEach((element) => {
        element.classList.add('poster-fade-out');
        setTimeout(() => element.remove(), 500);
      });
    }
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get posterElement() {
    return this.querySelector('[id^="Deferred-Poster-"]');
  }

  static get observedAttributes() {
    return ['playing'];
  }

  _stopOthersIfNeeded() {
    const otherPlayingVideos = Array.from(document.querySelectorAll('video-media')).filter(
      (el) => el !== this && el.hasAttribute('playing'),
    );

    if (otherPlayingVideos.length > 0) {
      otherPlayingVideos.forEach((el) => {
        if (el.isYouTubePlayer && el.player?.pauseVideo) {
          el.pauseYouTube();
        } else if (el.isVimeoPlayer && el.player?.pause) {
          el.pauseVimeo();
        } else if (el.videoElement && el.videoElement.pause) {
          el.pause();
        }

        el.removeAttribute('playing');
      });
    }

    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  }
}

// Define the custom element
customElements.define('video-media', VideoElement);

class SlideShow extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.mainslide = this.querySelector('[data-main-slide]');
    this.thumbs = this.querySelector('[data-has-thumbs]');
    this.initMainSlider = null;
    this.isFirstLoad = true; // Track first load
    this.init();
  }

  init() {
    const mainslideElement = this.querySelector('[data-swiper]');
    if (!mainslideElement) {
      return;
    }

    const swiperConfig = JSON.parse(mainslideElement.dataset.swiper || '{}');

    // Inject dynamic direction override for touch devices on hero-carousel
    if (this.mainslide.getAttribute('data-source') === 'hero-carousel') {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      swiperConfig.direction = isTouchDevice ? 'horizontal' : swiperConfig.direction;
    }

    this.initMainSlider = theme.SwiperSlide(mainslideElement, swiperConfig);

    // this.initMainSlider = theme.SwiperSlide(mainslideElement, JSON.parse(mainslideElement.dataset.swiper));

    if (!this.initMainSlider) {
      return;
    }
    const slides = this.querySelectorAll('[data-banner-slide-item]');

    const activeSlide = mainslideElement.querySelectorAll('.swiper-slide')[this.initMainSlider.activeIndex];
    if (!activeSlide) {
      return;
    }

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const { index } = event.target.dataset;
        this.initMainSlider.slideToLoop(parseInt(index) - parseInt(1));
      });
    }

    this.initMainSlider.on('slideChange', (swiper) => {
      const totalSlides = swiper.slides.length;
      const currentSlide = swiper.slides[swiper.activeIndex];
      /*if (this.isFirstLoad) {
        this.isFirstLoad = false;
        return;
      }*/

      if (mainslideElement.classList.contains('is-stable')) {
        mainslideElement.classList.remove('is-stable');
      }

      if (totalSlides > 0) {
        swiper.slides.forEach((slide) => slide.classList.remove('is-slide-active'));
      }

      currentSlide.classList.add('is-slide-active');
      this.handleVideoPlayback(currentSlide);
    });

    if (this.mainslide.getAttribute('data-source') === 'hero-carousel') {
      let isAnimating = false;
      const thumbs = this.thumbs?.querySelectorAll('[data-thumb] [data-progress]');

      // When initializing
      this.initMainSlider.on('init', (swiper) => {
        swiper.slides.forEach((slide) => {
          const img = slide.querySelector('.media-wrapper');
          if (img) gsap.set(img, { scale: 1.5 });
        });
        const activeSlide = swiper.slides[swiper.activeIndex];
        const activeImg = activeSlide.querySelector('.media-wrapper');
        activeSlide.classList.add('is-slide-active');
        if (activeImg) gsap.set(activeImg, { scale: 1 });
      });

      // When transition start
      this.initMainSlider.on('slideChangeTransitionStart', (swiper) => {
        if (isAnimating) return;
        isAnimating = true;

        if (this.thumbs) {
          // code for tab focus on thumbs
          const thumbSlides = this.thumbs.querySelectorAll('[data-thumb]');

          thumbSlides.forEach((thumb, index) => {
            thumb.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.initMainSlider.slideToLoop(index); // change to that slide
              }
            });
          });

          this.thumbs.classList.add('thumbs-disabled');
          thumbs.forEach((thumb) => {
            thumb.style.setProperty('--progress-path', '1');
          });
        }

        const totalSlides = swiper.slides.length;
        const nextIndex = (swiper.activeIndex + 1) % totalSlides;
        const previousIndex = swiper.previousIndex;

        const previousSlide = swiper.slides[previousIndex];
        const nextSlide = swiper.slides[nextIndex];
        const currentSlide = swiper.slides[swiper.activeIndex];

        if (previousSlide) {
          let prevSlideElement = previousSlide.querySelector('.slide-inner');
          let prevImageElement = previousSlide.querySelector('.media-wrapper');

          if (prevImageElement) {
            gsap.fromTo(prevImageElement, { scale: 1 }, { scale: 1.5, duration: 1, ease: 'power4.out' });
          }
        }

        if (currentSlide) {
          let currSlideElement = currentSlide.querySelector('.slide-inner');
          let currImageElement = currentSlide.querySelector('.media-wrapper');

          if (currImageElement) {
            gsap.fromTo(currImageElement, { scale: 1.5 }, { scale: 1, duration: 1, ease: 'power4.out' });
          }
        }
        if (nextSlide) {
          let nextSlideElement = nextSlide.querySelector('.slide-inner');
          let nextImageElement = nextSlide.querySelector('.media-wrapper');

          if (nextImageElement) {
            gsap.fromTo(nextImageElement, { scale: 1.5 }, { scale: 1, duration: 1, ease: 'power4.out' });
          }
        }
      });

      if (this.thumbs) {
        // When transition end thumbs enabled
        this.initMainSlider.on('slideChangeTransitionEnd', (swiper) => {
          isAnimating = false;
          if (this.thumbs) {
            this.thumbs.classList.remove('thumbs-disabled');
          }
        });

        // preogress bar when autoplay
        this.initMainSlider.on('autoplayTimeLeft', (swiper, timeLeft, percentage) => {
          const activeThumb = thumbs[swiper.realIndex];
          if (activeThumb) {
            activeThumb.style.setProperty('--progress-path', `${1 - (1 - percentage)}`);
          }
        });
      }
    }

    this.setupNavigation();
  }

  setupNavigation() {
    const arrowControls = this.querySelector('[data-arrow-controls]');
    if (!arrowControls) return;

    const prevBtn = this.querySelector('[data-prev-slide]');
    const nextBtn = this.querySelector('[data-next-slide]');

    prevBtn?.addEventListener('click', () => this.initMainSlider.slidePrev());
    nextBtn?.addEventListener('click', () => this.initMainSlider.slideNext());
  }

  jumpToSlide(index) {
    if (this.initMainSlider) {
      this.initMainSlider.slideToLoop(index);
    } else {
      console.warn('🚨 Cannot jump to slide: Swiper is not initialized.');
    }
  }

  handleVideoPlayback(activeSlide) {
    // Find video elements in the active slide
    const videoElement = activeSlide.querySelector('video');
    if (videoElement) {
      videoElement.play().catch((error) => {
        console.warn('Error attempting to play video:', error);
      });
    }
  }

  pauseAllVideos() {
    document.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }
}

customElements.define('slide-show', SlideShow);

class Mediagallery extends HTMLElement {
  constructor() {
    super();
    this.initMainSlider = null;
    this.primarySlider = this.querySelector('[data-product-main-slider]');
    this.section = this.closest('.shopify-section');
    this.thumbnailSlider = this.querySelector('[data-product-thumbnail-slider]');
    this.isInitialized = false; // Flag to track initialization
    this.init();
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  init() {
    if (!this.primarySlider) return;

    const primaryInstance = theme.SwiperSlide(
      this.primarySlider,
      JSON.parse(this.primarySlider.getAttribute('data-swiper')),
    );
    this.initMainSlider = primaryInstance;

    if (this.thumbnailSlider) {
      const thumbnailInstance = theme.SwiperSlide(
        this.thumbnailSlider,
        JSON.parse(this.thumbnailSlider.getAttribute('data-swiper')),
      );
      primaryInstance.thumbs.swiper = thumbnailInstance;
      primaryInstance.thumbs.init();
      thumbnailInstance.params.mousewheel = true;

      if (this.dataset.source == 'quick-view') {
        const index = primaryInstance.activeIndex;
        thumbnailInstance.slideToLoop(parseInt(index));
      }
    }

    // Enable mouse wheel on both sliders
    primaryInstance.params.mousewheel = true;

    // Store the active slide index to avoid triggering the slideChange on initialization
    const activeSlideIndex = this.primarySlider.querySelectorAll('.swiper-slide')[primaryInstance.activeIndex];
    const activeSlideValue = activeSlideIndex.getAttribute('data-index');

    const handleSlideChange = () => {
      if (!this.isInitialized) {
        this.isInitialized = true; // Mark the gallery as initialized
        return; // Skip the first run on load
      }

      if (this.primarySlider) {
        const activeSlide = this.primarySlider.querySelectorAll('.swiper-slide')[primaryInstance.activeIndex];

        const imageDataFeature = activeSlide?.querySelector('img');
        const imgSrcData = imageDataFeature?.src;

        if (this.section?.querySelector('[feature-product-main-image]')) {
          this.section.querySelector('[feature-product-main-image]').querySelector('img').src = imgSrcData;
        }

        this.playActiveMedia(activeSlide);

        if (activeSlide.getAttribute('data-media-type') === 'model') {
          primaryInstance.allowTouchMove = false;
          primaryInstance.draggable = false;

          activeSlide.addEventListener('mouseenter', () => {
            lockBodyScroll();
          });

          activeSlide.addEventListener('mouseleave', () => {
            unlockBodyScroll();
          });
        } else {
          primaryInstance.allowTouchMove = true;
          primaryInstance.draggable = true;
        }

        this.viewInSpaceButton &&
          (activeSlide.getAttribute('data-media-type') === 'model'
            ? this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeSlide.getAttribute('data-media-id'))
            : this.viewInSpaceButton.setAttribute(
                'data-shopify-model3d-id',
                this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'),
              ));
      }
    };

    primaryInstance.on('slideChange', handleSlideChange);
  }

  playActiveMedia(activeMedia) {
    const deferredMediaElement = activeMedia.querySelector('video-media');
    window.stopAllSiteVideos();
    if (deferredMediaElement) {
      this.removePoster(activeMedia);

      setTimeout(() => {
        if (!deferredMediaElement.getAttribute('loaded')) {
          const content = document.createElement('div');
          content.appendChild(deferredMediaElement.querySelector('template').content.firstElementChild.cloneNode(true));

          deferredMediaElement.setAttribute('loaded', true);
          const deferredElement = deferredMediaElement.appendChild(content.querySelector('video, iframe'));

          if (focus) deferredElement.focus();

          if (deferredElement.nodeName === 'VIDEO') {
            deferredElement.play().catch((error) => {
              console.warn('Autoplay failed:', error);
            });
          }
        }
      }, 200);
    } else {
      const deferredModal = activeMedia.querySelector('model-viewer');
    }
  }

  removePoster(posterElement) {
    const posterContainer = posterElement;
    if (posterContainer) {
      posterContainer.style.transition = 'opacity 0.5s ease-in-out';
      const elementsToRemove = [
        posterContainer.querySelector('img'),
        posterContainer.querySelector('[data-poster-button]'),
      ].filter(Boolean);

      elementsToRemove.forEach((element) => {
        element.classList.add('poster-fade-out');
        setTimeout(() => element.remove(), 500);
      });
    }
  }

  jumpToSlide(index) {
    if (this.initMainSlider) {
      this.initMainSlider.slideToLoop(index);
    } else {
      console.warn('Splide instance not initialized');
    }
  }
}

customElements.define('media-gallery', Mediagallery);

class MediaFeaturedgallery extends HTMLElement {
  constructor() {
    super();
    this.handleSplide();
    this.primarySlider = this.querySelector('[data-product-main-slider]');
    this.section = this.closest('.shopify-section');
    this.thumbnailSlider = this.section.querySelector('[data-product-thumbnail-slider]');
  }

  handleSplide() {
    if (this.primarySlider && this.thumbnailSlider) {
      const primaryInstance = theme.SwiperSlide(
        this.primarySlider,
        JSON.parse(this.primarySlider.getAttribute('data-swiper')),
      );
      this.thumbnailInstance = theme.Swiper(
        this.thumbnailSlider,
        JSON.parse(this.thumbnailSlider.getAttribute('data-swiper')),
      );

      primaryInstance.sync(this.thumbnailInstance);

      primaryInstance.on('slideChange', function () {
        // window.stopAllSiteVideos();
        const active_slide = this.primarySlider.querySelector('.swiper-slide-active');
        let imageDataFeature = active_slide.querySelector('img');
        let imgSrcData = imageDataFeature ? imageDataFeature.src : null;

        if (section.querySelector('[feature-product-main-image]')) {
          section.querySelector('[feature-product-main-image]').querySelector('img').src = imgSrcData;
        }
        //this.closest('.shopify-section');
      });
    } else {
      if (primarySlider) {
        const primaryInstance = theme.SwiperSlide(primarySlider, JSON.parse(primarySlider.getAttribute('data-swiper')));
        //primaryInstance.mount();
      }
    }
  }
}
customElements.define('media-featured-gallery', MediaFeaturedgallery);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));
    this.prevWidth = window.innerWidth;
    // this.handleResize = this.handleResize.bind(this);
    // window.addEventListener('resize', this.handleResize);

    this._boundHandleResize = theme.utils.debounce(this.handleResize.bind(this), 300);
    window.addEventListener('resize', this._boundHandleResize);

    this.mutationObserver = new MutationObserver(() => {
      if (this.open && this.isConnected) {
        this.handleResize(); // force recalc height
      }
    });

    this.mutationObserver.observe(this.contentElement, {
      childList: true,
      subtree: true,
    });

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();

    // Close other accordions when this one is opened
    if (value) {
      this.closeOtherAccordions();
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    requestAnimationFrame(() => {
      this.open = !this.open;
    });
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  handleResize() {
    const currentWidth = window.innerWidth;
    const wasMobile = this.prevWidth <= 768;
    const isDesktop = currentWidth > 768;
    const inFooter = this.closest('.shopify-section')?.classList.contains('footer-section');
    // OPEN: mobile → desktop (footer only)
    if (wasMobile && isDesktop && inFooter) {
      this.open = true;
    }
    // CLOSE: desktop → mobile (footer only)
    if (!wasMobile && !isDesktop && inFooter) {
      this.open = false;
    }
    // Save for next comparison
    this.prevWidth = currentWidth;

    if (!this.open || !this.isConnected) return;

    const summaryHeight = this.summaryElement.clientHeight;
    const newContentHeight = this.contentElement.scrollHeight;
    const newHeight = summaryHeight + newContentHeight;

    const currentHeight = this.clientHeight;

    if (Math.abs(currentHeight - newHeight) < 2) return;

    this.style.overflow = 'hidden';
    this.style.height = `${currentHeight}px`;

    requestAnimationFrame(() => {
      const animation = this.animate(
        { height: [`${currentHeight}px`, `${newHeight}px`] },
        {
          duration: 250,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        this.style.height = 'auto';
        this.style.overflow = '';
      };
    });
  }

  transition(value) {
    const summaryHeight = this.summaryElement.clientHeight;
    const contentHeight = this.contentElement.scrollHeight;
    const currentHeight = this.clientHeight;

    this.style.willChange = 'height';
    this.contentElement.style.willChange = 'opacity, transform';
    this.style.overflow = 'hidden';

    if (value) {
      // Force open instantly to measure natural height
      this.setAttribute('open', '');
      this.style.height = `${summaryHeight}px`;
      this.contentElement.style.opacity = '0';
      this.contentElement.style.transform = 'translateY(10px)';

      // Let browser paint it
      requestAnimationFrame(() => {
        const expandedHeight = summaryHeight + this.contentElement.scrollHeight;

        // Animate height
        this.animate(
          { height: [`${summaryHeight}px`, `${expandedHeight}px`] },
          {
            duration: 300,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'forwards',
          },
        ).onfinish = () => {
          this.style.height = 'auto';
          this.style.overflow = '';
          this.style.willChange = '';
          this.style.transform = '';
        };

        // Animate content in
        this.contentElement.animate(
          {
            opacity: [0, 1],
            transform: ['translateY(10px)', 'translateY(0)'],
          },
          {
            duration: 250,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'forwards',
          },
        );
      });
    } else {
      // Closing animation
      const closingHeight = `${currentHeight}px`;
      const collapsedHeight = `${summaryHeight}px`;

      const animation = this.animate(
        { height: [closingHeight, collapsedHeight] },
        {
          duration: 250,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        },
      );

      animation.onfinish = () => {
        this.removeAttribute('open');
        this.style.height = 'auto';
        this.style.overflow = '';
        this.style.willChange = '';
        this.style.transform = '';
      };

      this.contentElement.animate(
        {
          opacity: [1, 0],
          transform: ['translateY(0)', 'translateY(10px)'],
        },
        {
          duration: 200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        },
      ).onfinish = () => {
        this.contentElement.style.opacity = '0';
        this.contentElement.style.willChange = '';
        this.contentElement.style.transform = '';
      };
    }
    setTimeout(() => {
      refreshScrollTriggersForSubsequentSections(this);
      this.handleResize();
    }, 300);
  }

  closeOtherAccordions() {
    // Close all other accordion items within the same container
    const parent = this.closest('[data-accordion-container]');
    if (parent) {
      const allAccordions = parent.querySelectorAll('details[is="accordion-details"]');
      allAccordions.forEach((accordion) => {
        if (accordion !== this && accordion.open) {
          accordion.open = false; // Trigger the close logic for this accordion
        }
      });
    }
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent('toggle', { bubbles: true, detail: { current: this } }),
    );
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this._boundHandleResize);
    this.mutationObserver.disconnect();
  }
}

customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class RotatingText extends HTMLElement {
  constructor() {
    super();
    this.textElement = this.querySelector('[data-text]');
    this.lastExecutionTime = 0;
    this.resizeRunning = false;

    if (this.textElement) {
      this.applyRotation();
    }

    this.onResize = this.onResize.bind(this);
    this.checkResizeDone = this.checkResizeDone.bind(this);
  }

  connectedCallback() {
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    this.lastExecutionTime = performance.now();

    if (!this.resizeRunning) {
      this.resizeRunning = true;
      requestAnimationFrame(this.checkResizeDone);
    }
  }

  checkResizeDone() {
    const now = performance.now();

    // If no resize event fired in the last 150ms, we assume user stopped resizing
    if (now - this.lastExecutionTime < 150) {
      requestAnimationFrame(this.checkResizeDone);
    } else {
      this.resizeRunning = false;
      this.resetAndReapply(); // Only run when resizing is done
    }
  }

  resetAndReapply() {
    const originalText = this.textElement.textContent;
    this.textElement.innerHTML = originalText;
    this.applyRotation();
  }

  applyRotation() {
    const originalText = this.textElement.innerText;

    this.textElement.innerHTML = originalText
      .split('')
      .map((char, index) => `<span style="transform:rotate(${index * 10.5}deg)">${char}</span>`)
      .join('');
  }
}

customElements.define('rotating-text', RotatingText);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      (this.updateSelectionMetadata(event.target),
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        }));
    });
  }
  updateSelectionMetadata(target) {
    target.tagName === 'SELECT' &&
      target.selectedOptions.length &&
      (Array.from(target.options)
        .find((option) => option.hasAttribute('selected'))
        .removeAttribute('selected'),
      target.selectedOptions[0].setAttribute('selected', ''));
  }
  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }
  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map((selector) =>
      selector.getAttribute('data-option-value-id'),
    );
  }
}
customElements.define('variant-selects', VariantSelects);

class ProductInfoToggle extends HTMLButtonElement {
  constructor() {
    super();
    this.section = this.closest('section');
  }
  connectedCallback() {
    this.handleExpandCollapse();
  }

  handleExpandCollapse() {
    const mainRow = this.section?.querySelector('[data-main-row]');

    if (this && mainRow) {
      this.addEventListener('click', () => {
        const isExpanded = mainRow.classList.toggle('expand');
        mainRow.classList.toggle('collapse', !isExpanded);
        // Update the data attribute based on the state
        mainRow.setAttribute('data-main-row', isExpanded ? 'expand' : 'collapse');
        setTimeout(() => {
          refreshScrollTriggersForSubsequentSections(this);
        }, 400);
      });
    }
  }
}
customElements.define('toggle-info', ProductInfoToggle, { extends: 'button' });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return parseInt(this.dataset.speed || 16);
  }

  get maximum() {
    return parseInt(this.dataset.maximum || Math.ceil(this.parentWidth / this.childElementWidth) + 4);
  }

  get direction() {
    return this.dataset.direction || 'left';
  }
  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.childElementCount === 1) {
      const maximumClones = parseInt(this.maximum);
      if (isNaN(maximumClones) || maximumClones <= 0) {
        //this.maximum = 4;
      }
      for (let index = 0; index < maximumClones; index++) {
        const clone = this.childElement.cloneNode(true);
        this.appendChild(clone);
        //this.style.setProperty('--marquee-speed', `${this.speed}s`);
      }
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('marquee-element', MarqueeElement);

class ScrollTop extends HTMLElement {
  constructor() {
    super();
    this.currentScrollTop = 0;
    this.BodyScrollTop = 0;
    this.scrollButton = this.querySelector('[back-to-top-button]');

    window.addEventListener('scroll', this.onScroll.bind(this), false);
    this.scrollButton.addEventListener('click', this.onClick.bind(this));
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 1000) {
      if (scrollTop > this.currentScrollTop) {
        this.scrollButton.classList.add('show');
      }
    } else {
      this.scrollButton.classList.remove('show');
    }
    this.currentScrollTop = scrollTop;
  }

  onClick() {
    this.smoothScrollToTop();
  }

  smoothScrollToTop() {
    const duration = 700;
    const start = window.pageYOffset;
    const startTime = performance.now();

    const scrollStep = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easing = this.easeInOutQuad(progress);

      window.scrollTo(0, start - start * easing);

      if (timeElapsed < duration) {
        requestAnimationFrame(scrollStep);
      } else {
        if (typeof ScrollTrigger !== 'undefined' && window.innerWidth >= 768) {
          ScrollTrigger.refresh();
          const dataVCElement = document.querySelectorAll('[vertical-circle-rotate]');
          dataVCElement.forEach((vcItmes, index) => {
            const allItemVerticalSl = vcItmes.querySelectorAll('[vertical-circle-item]');
            gsap.set(allItemVerticalSl, { opacity: 0 });
            gsap.set(allItemVerticalSl[0], { opacity: 1 });
          });
        }
      }
    };

    requestAnimationFrame(scrollStep);
  }

  // Easing function for smooth scroll animation
  easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }
}

customElements.define('scroll-top', ScrollTop);

class GridHover extends HTMLDivElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.background = this.querySelector('[data-background]');
    this.isAnimating = false;
    this.addEventListeners();
    this.handleCursorMove();
  }
  handleCursorMove() {
    // Ensure `this.section` and `[data-cursor-wrapper]` exist
    if (!this.section) {
      console.warn('Section element not found for GridHover.');
      return;
    }

    const cursorWrapper = this.section.querySelector('[data-cursor-wrapper]');
    const gridWrapper = this.section.querySelector('[data-grid-wrapper]');

    if (!cursorWrapper) {
      console.warn('Cursor wrapper element not found in the section.');
      return;
    }

    // Apply GSAP animations to the cursor
    gsap.set(cursorWrapper, { xPercent: -50, yPercent: -50 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.6, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.6, ease: 'power3' });

    gridWrapper.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });
    gridWrapper.addEventListener('mouseleave', (e) => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.4 });
    });
    gridWrapper.addEventListener('mouseenter', (e) => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.4 });
    });
  }

  addEventListeners() {
    this.addEventListener('mouseenter', (e) => this.handleMouseEnter(e));
    this.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
  }

  handleMouseEnter(event) {
    // if (this.isAnimating) return;
    const direction = this.getMouseEntryDirection(event);
    this.isAnimating = true;

    gsap.fromTo(
      this.background,
      {
        y: direction === 'top' ? '-100%' : '100%',
        transformOrigin: direction === 'top' ? '100% 0%' : '0% 100%',
      },
      {
        y: '0%',
        transformOrigin: direction === 'top' ? '100% 0%' : '0% 100%',
        duration: 0.5,
        ease: 'power2.inOut',
        overwrite: 'auto',
        // onComplete: () => {
        //   this.isAnimating = false;
        // },
      },
    );
  }

  handleMouseLeave(event) {
    // if (this.isAnimating) return;
    const direction = this.getMouseEntryDirection(event);
    this.isAnimating = true;

    gsap.to(this.background, {
      y: direction === 'top' ? '-100%' : '100%',
      transformOrigin: direction === 'top' ? '0% 100%' : '100% 0%',
      duration: 0.5,
      ease: 'power2.inOut',
      overwrite: 'auto',
      // onComplete: () => {
      //   this.isAnimating = false;
      // },
    });
  }

  getMouseEntryDirection(event) {
    const rect = this.getBoundingClientRect();
    const mouseY = event.clientY;
    return mouseY < rect.top + this.offsetHeight / 2 ? 'top' : 'bottom';
  }
}

customElements.define('grid-hover', GridHover, { extends: 'div' });

class CountdownElement extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.endDate = this.dataset.endDate;
    this.init();
  }

  init() {
    if (!this.endDate) return;
    this.endTime = new Date(this.endDate.split('/').reverse().join('/')).getTime();
    this.timerInterval = null;
    this.setupNumberPacks();
    this.startCountdown();
  }

  setupNumberPacks() {
    const daysWrapper = this.querySelector('[data-days]');
    const hoursWrapper = this.querySelector('[data-hours]');
    const minutesWrapper = this.querySelector('[data-minutes]');
    const secondsWrapper = this.querySelector('[data-seconds]');

    if (!daysWrapper || !hoursWrapper || !minutesWrapper || !secondsWrapper) return;

    const daysDiff = Math.ceil((this.endTime - Date.now()) / (1000 * 60 * 60 * 24));
    let daysDigits = daysDiff < 10 ? 2 : daysDiff.toString().length; // Minimum 2 digits for days
    const timeDigits = 2; // Fixed to 2 digits for hours, minutes, and seconds

    this.createNumberPacks(daysWrapper, daysDigits, 'days', 9);
    this.createNumberPacks(hoursWrapper, timeDigits, 'hours', 2);
    this.createNumberPacks(minutesWrapper, timeDigits, 'minutes', 5);
    this.createNumberPacks(secondsWrapper, timeDigits, 'seconds', 5);
  }

  createNumberPacks(wrapper, digits, type, maxFirstDigit) {
    for (let i = 0; i < digits; i++) {
      const numbersPack = document.createElement('div');
      numbersPack.classList.add(`number-pack`, `numbers-pack-${type}`);

      const maxDigit = i === 0 ? maxFirstDigit : 9;
      numbersPack.style.setProperty('--data-numbers', maxDigit);

      // Create numbers in reverse order
      for (let j = maxDigit; j >= 0; j--) {
        const span = document.createElement('span');
        span.classList.add('count-number');
        span.textContent = j; // Ensure numbers are visible
        numbersPack.appendChild(span);
      }

      wrapper.appendChild(numbersPack);
    }
  }

  startCountdown() {
    const updateCountdown = () => {
      const now = Date.now();
      const timeRemaining = this.endTime - now;

      if (timeRemaining <= 0) {
        this.stopCountdown(); // Clear interval and handle cleanup
        this.updateCountdown(0, 0, 0, 0); // Set to zero when the timer ends
        this.handleCountdownEnd();
        return;
      }

      const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

      this.updateCountdown(days, hours, minutes, seconds);
    };

    updateCountdown(); // Initial render
    this.timerInterval = setInterval(updateCountdown, 1000);
  }

  stopCountdown() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  updateCountdown(days, hours, minutes, seconds) {
    this.animateUnit(this.querySelector('[data-days]'), days);
    this.animateUnit(this.querySelector('[data-hours]'), hours);
    this.animateUnit(this.querySelector('[data-minutes]'), minutes);
    this.animateUnit(this.querySelector('[data-seconds]'), seconds);
  }

  animateUnit(wrapper, value) {
    if (!wrapper) return;

    const valueStr = value.toString().padStart(wrapper.childElementCount, '0');
    Array.from(wrapper.children).forEach((pack, index) => {
      const digit = parseInt(valueStr[index], 10) || 0;
      // const translateY = `${digit * 60}px`; // Move based on digit height
      const translateY = `${digit * 100}%`; // Move based on digit height
      pack.style.transform = `translateY(${translateY})`;
      // pack.style.setProperty('--translate-y', translateY);

      pack.style.transition = 'transform 0.5s ease';
    });
  }

  handleCountdownEnd() {
    const endNote = this.section.querySelector('[data-end-note]');
    if (endNote) {
      endNote.style.display = 'block'; // Show the end note
    }
    this.remove(); // Automatically remove the countdown element
  }

  // Explicit cleanup to handle removal
  removeCountdown() {
    this.stopCountdown();
    this.remove();
  }
}

customElements.define('count-down', CountdownElement);

class CountryProvince extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');

    this.countryElement.addEventListener('change', this.CountryChange.bind(this));

    const defaultCountry = this.countryElement.getAttribute('data-default');
    const defaultProvince = this.provinceElement.getAttribute('data-default');

    if (defaultCountry) {
      this.setSelectedOption(this.countryElement, defaultCountry);
    }

    this.CountryChange(defaultProvince);
  }

  setSelectedOption(selectElement, value) {
    const selectedIndex = Array.from(selectElement.options).findIndex((option) => option.value === value);
    selectElement.selectedIndex = selectedIndex !== -1 ? selectedIndex : 0;
    selectElement.dispatchEvent(new Event('change', { bubbles: true }));
  }

  CountryChange(defaultProvince = null) {
    const option = this.countryElement.options[this.countryElement.selectedIndex];
    const provinces = JSON.parse(option.dataset.provinces || '[]');

    const shouldHide = provinces.length === 0;
    this.provinceElement.parentElement.hidden = shouldHide;
    this.provinceElement.closest('.form-group').classList.toggle('hidden', shouldHide);

    this.provinceElement.innerHTML = ''; // Clear previous options

    if (!shouldHide) {
      provinces.forEach(([value, name]) => {
        const selected = defaultProvince ? value === defaultProvince : false;
        this.provinceElement.options.add(new Option(name, value, selected, selected));
      });

      // Set the selected province only if a default is provided
      if (defaultProvince) {
        this.setSelectedOption(this.provinceElement, defaultProvince);
      }
    }
  }
}

customElements.define('country-province', CountryProvince);

class ShippingCalculator extends HTMLElement {
  constructor() {
    (super(), this._setupCountries());
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.querySelector('[data-shipping-result]');
    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }
  handleFormSubmit(event) {
    event.preventDefault();
    const zip = this.querySelector('[name="address[zip]"]').value;
    const country = this.querySelector('[name="address[country]"]').value;
    const province = this.querySelector('[name="address[province]"]').value;
    const body = JSON.stringify({
      shipping_address: {
        zip,
        country,
        province,
      },
    });

    let sectionUrl = `${routes.cart_url}/shipping_rates.json`;
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, {
      ...theme.utils.fetchConfig(),
      ...{ body },
    })
      .then((response) => response.json())
      .then((parsedState) => {
        parsedState.shipping_rates
          ? this.formatShippingRates(parsedState.shipping_rates)
          : this.formatError(parsedState);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        ((this.resultsElement.hidden = !1), this.submitButton.removeAttribute('aria-busy'));
      });
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map((rate) => {
      const { presentment_name, currency, price } = rate;
      return `<div class="cart-form-message success">${presentment_name}: ${currency} ${price}</div>`;
    });

    this.resultsElement.innerHTML = `${shippingRatesList.join('')}`;
    this.resultsElement.classList.remove('hidden');
  }

  formatError(errors) {
    const errorMessages = Object.values(errors)
      .map((error) => `<div class="cart-form-message error text-xxs">${error}</div>`)
      .join('');
    this.resultsElement.innerHTML = `${errorMessages}`;
    this.resultsElement.classList.remove('hidden');
  }

  _setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector('[shippingCountry]', '[shippingProvince]', {
        hideElement: '[shippingProvince]',
      });
    }
  }
}
customElements.define('shipping-calculator', ShippingCalculator);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = theme.utils.debounce((event) => {
      this.onChange(event);
    }, 400);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    shippingconversion();
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    if (!event.target) return;
    const inputValue = parseInt(event.target.value);
    if (!inputValue) return;
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '[cart-drawer-footer]', '[data-cart-shippingbar]'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);

            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          const countElement = document.querySelector('cart-drawer-items');
          const count = countElement.getAttribute('data-items');
          const cartIcons = document.querySelectorAll('[data-cart-icon-bubble]');
          const cartDrawerShipping = document.querySelector('cart-shipping');
          if (cartIcons.length > 0) {
            // Check if there are any cart icons
            cartIcons.forEach((cartIcon) => {
              if (cartIcon.classList.contains('is-empty-cart')) {
                // Check if the cart icon is marked as empty
                const hiddenSpan = document.createElement('span');
                hiddenSpan.className = 'visually-hidden';
                hiddenSpan.textContent = 'Cart';

                const countBubble = document.createElement('div');
                countBubble.className = 'cart-count-bubble';

                const countSpan = document.createElement('span');
                countSpan.setAttribute('aria-hidden', 'true');
                countSpan.setAttribute('data-total-cart-item', '');
                countSpan.textContent = count;

                cartIcon.appendChild(hiddenSpan);
                cartIcon.appendChild(countBubble);

                countBubble.appendChild(countSpan);

                // Remove the 'is-empty-cart' class to show the cart has items
                cartIcon.classList.remove('is-empty-cart');
                if (cartDrawerShipping) {
                  cartDrawerShipping.classList.remove('is-empty-cart');
                }
              } else {
                document.querySelectorAll('[data-total-cart-item]').forEach((element) => {
                  element.innerHTML = count;
                });
                if (count > 0) {
                  document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
                    bubble.classList.remove('hidden');
                  });
                } else {
                  document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
                    bubble.classList.add('hidden');
                  });
                }
              }
            });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=${this.dataset.sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
    shippingconversion();
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.querySelector('[data-main-cart-items]')
          ? document.querySelector('[data-main-cart-items]').dataset.id
          : null,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.querySelector('[data-main-cart-footer]')
          ? document.querySelector('[data-main-cart-footer]').dataset.id
          : null,
        selector: '[data-footer-content]',
      },
      {
        id: 'main-cart-shipping',
        section: document.querySelector('[data-cart-shipping]')
          ? document.querySelector('[data-cart-shipping]').dataset.id
          : null,
        selector: '[data-cart-shipping]',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    const activeElement = document.activeElement;
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartDrawerShipping = document.querySelector('cart-shipping');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerShipping) cartDrawerShipping.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
        cartCountBubbles.forEach((bubble) => {
          if (parsedState.item_count === 0) {
            bubble.classList.add('hidden');
          } else {
            bubble.classList.remove('hidden');
          }
        });

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector,
          );
          //code updated on 15 april
          const lineItem =
            document.getElementById(`cart-product-item-${line}`) ||
            document.getElementById(`cart-item-quantity-${line}`);

          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, cartDrawerWrapper.querySelector('a'));
          }
          // end
        });

        // activeElement.focus();
        shippingconversion();
        document.querySelectorAll('[data-total-cart-item]').forEach((element) => {
          element.innerHTML = parsedState.item_count;
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {})
      .finally(() => {});
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`cart-item-quantity-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      // Remove existing error element if it exists
      const existingError = lineItemError.querySelector('.error');
      if (existingError) {
        existingError.remove();
      }

      // Create new error element
      const errorElement = document.createElement('span');
      errorElement.classList.add('error', 'text-xxs');
      errorElement.textContent = message;
      lineItemError.appendChild(errorElement);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-items', CartItems);
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-cart-drawer]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[cart-drawer-footer]',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-cart-shipping]',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      this.closest(`[id="cart-product-item-${this.dataset.index}"]`).classList.add('zoom');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class RemoveGiftWrap extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');

      const giftWrapping = document.querySelector('gift-wrapping');
      giftWrapping.removeGiftWrap();
    });
  }
}
customElements.define('remove-gift-wrap', RemoveGiftWrap, { extends: 'a' });

class CartQuantitySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.isUpdating = false;
    this.input = this.querySelector('input');
    this.updateButtonState();
    this.handleShowMore();
    this.buttons = Array.from(this.querySelectorAll('[data-quantity-button]'));

    // Attach click listeners to + and - buttons
    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));

    // Separate change handler for manual input changes
    this.addEventListener('change', this.onInputChange.bind(this));
  }

  onButtonClick(event) {
    event.preventDefault();
    // Prevent multiple rapid updates
    if (this.isUpdating) return;
    this.isUpdating = true;

    const previousValue = this.input.value;
    const step = parseInt(this.input.step) || 1;
    const currentValue = parseInt(this.input.value) || 1;

    if (event.currentTarget.name === 'plus') {
      this.input.value = currentValue + step;
    } else {
      // Ensure value doesn't drop below 1
      this.input.value = Math.max(1, currentValue - step);
    }

    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    this.input.dispatchEvent(changeEvent);

    this.updateButtonState();

    setTimeout(() => {
      this.isUpdating = false;
    }, 10);
  }

  onInputChange(event) {
    // Ensure input value stays within valid range
    const min = 1;
    let currentValue = parseInt(this.input.value);
    if (isNaN(currentValue) || currentValue < min) {
      this.input.value = min;
    }

    this.updateButtonState();

    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    this.input.dispatchEvent(changeEvent);
  }

  updateButtonState() {
    const minusButton = this.querySelector('[name="minus"]');
    const plusButton = this.querySelector('[name="plus"]');

    const value = parseInt(this.input.value) || 1;

    // Disable minus button if value is at minimum
    // minusButton.disabled = value <= 1;
  }

  get showMoreButton() {
    return this.closest('section')?.querySelector('show-more-button button');
  }

  handleShowMore() {
    const button = this.showMoreButton;

    if (button) {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const pricingSection = button.closest('volume-pricing');
        if (!pricingSection) return;

        pricingSection.querySelectorAll('.show-more-item.hidden').forEach((hiddenItem) => {
          hiddenItem.classList.remove('hidden', 'show-more-item');
        });

        button.classList.add('hidden');
      });
    }
  }
}

customElements.define('cart-quantity-set', CartQuantitySelector);

class GiftWrapping extends HTMLElement {
  constructor() {
    super();

    this.giftWrapId = this.dataset.giftWrapId;
    this.giftWrapping = this.dataset.giftWrapping;
    this.cartItemsSize = parseInt(this.getAttribute('cart-items-size'));
    this.giftWrapsInCart = parseInt(this.getAttribute('gift-wraps-in-cart'));
    this.itemsInCart = parseInt(this.getAttribute('items-in-cart'));
  }

  connectedCallback() {
    // When the gift-wrapping checkbox is checked or unchecked.
    this.querySelector('[name="attributes[gift-wrapping]"]').addEventListener('change', (event) => {
      event.target.checked ? this.setGiftWrap() : this.removeGiftWrap();
    });

    if (this.cartItemsSize == 1 && this.giftWrapsInCart > 0) {
      return this.removeGiftWrap();
    }
  }

  setGiftWrap() {
    const body = JSON.stringify({
      updates: {
        [this.giftWrapId]: 1,
      },
      attributes: {
        'gift-wrapping': true,
      },
    });

    this.fetchGiftWrap(body);
  }

  removeGiftWrap() {
    const body = JSON.stringify({
      updates: {
        [this.giftWrapId]: 0,
      },
      attributes: {
        'gift-wrapping': '',
      },
    });

    this.fetchGiftWrap(body);
  }

  fetchGiftWrap(body) {
    fetch(`${window.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
        const cartWrapper = document.querySelector('cart-items');
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartDrawerShipping = document.querySelector('cart-shipping');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartWrapper) cartWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerShipping) cartDrawerShipping.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
      })
      .catch((error) => {
        console.log(error);
      });
  }
}
customElements.define('gift-wrapping', GiftWrapping);

class CopyToClipboard extends HTMLButtonElement {
  constructor() {
    super();
    this.copyId = this.dataset.id; // Get the `data-id` attribute value
    this.copiedText = this.dataset.copied || 'Copied!'; // Text to show when copied
    this.init();
  }

  init() {
    this.addEventListener('click', this.handleCopy.bind(this));
  }

  async handleCopy() {
    if (!this.copyId) {
      console.error('No data-id found to copy!');
      return;
    }

    try {
      // Attempt to use the Clipboard API
      await navigator.clipboard.writeText(this.copyId);
      this.showCopiedMessage();
    } catch (error) {
      // Fallback to execCommand if Clipboard API fails
      console.warn('Clipboard API failed, falling back to execCommand:', error);
      this.fallbackCopy();
    }
  }

  fallbackCopy() {
    const tempInput = document.createElement('input');
    tempInput.value = this.copyId;
    tempInput.style.position = 'absolute';
    tempInput.style.left = '-9999px';
    document.body.appendChild(tempInput);

    tempInput.select();
    document.execCommand('copy'); // Deprecated but used as a fallback
    document.body.removeChild(tempInput);

    this.showCopiedMessage();
  }

  showCopiedMessage() {
    const copiedMessage = document.createElement('div');
    copiedMessage.textContent = this.copiedText;
    copiedMessage.className = 'copied-message';
    copiedMessage.style.opacity = '0';

    this.closest('[data-clipboard-wrapper]').appendChild(copiedMessage);

    setTimeout(() => {
      copiedMessage.style.opacity = '1';
    }, 0);

    setTimeout(() => {
      copiedMessage.style.opacity = '0';
      copiedMessage.addEventListener('transitionend', () => {
        // this.closest('[data-clipboard-wrapper]').removeChild(copiedMessage);
      });
    }, 2000);
  }
}

customElements.define('copy-to-clipboard', CopyToClipboard, { extends: 'button' });

class CollectionSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.properties = this.querySelector('[data-swiper]');
    if (!this.properties) return;
    this.primaryInstance = theme.SwiperSlide(this.properties, JSON.parse(this.properties.getAttribute('data-swiper')));
  }
}
customElements.define('collection-slider', CollectionSlider);

class SocialMediaSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.properties = this.dataset.swiper;
    if (!this.properties) return;
    this.primaryInstance = theme.SwiperSlide(this, JSON.parse(this.properties));
  }
}
customElements.define('social-media-slider', SocialMediaSlider);

class FeaturedProducts extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.properties = this.dataset.swiper;
    if (!this.properties) return;
    this.primaryInstance = theme.SwiperSlide(this, JSON.parse(this.properties));
  }
}
customElements.define('featured-products', FeaturedProducts);

if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientfields = this.querySelector('[data-recipient-fields]');
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form-recipient-error-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        const giftTriggers = document.querySelectorAll('[data-gift-form]');

        giftTriggers.forEach((trigger) => {
          const toggleCheckbox = () => {
            const checkbox = trigger.closest('label')?.previousElementSibling;

            if (checkbox && checkbox.type === 'checkbox') {
              // Toggle the checkbox checked state
              checkbox.checked = !checkbox.checked;
              checkbox.dispatchEvent(new Event('change', { bubbles: true }));

              // Wait for form to open, then focus a field inside (if checked)
              if (checkbox.checked) {
                setTimeout(() => {
                  const giftForm = document.querySelector('#gift-form'); // adjust selector as needed
                  const firstInput = giftForm?.querySelector('input, textarea, select');
                  if (firstInput) {
                    firstInput.focus();
                  }
                }, 50); // slight delay if form is revealed via CSS or JavaScript
              }
            }
          };

          // Trigger on keyboard (Enter or Space)
          trigger.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault(); // prevent scrolling on spacebar
              toggleCheckbox();
            }
          });
        });

        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.recipientfields.classList.add('open');
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.recipientfields.classList.remove('open');
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error');

            if (!errorTextElement) return;
            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form-message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    },
  );
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');

    if (this.input) {
      this.input.addEventListener(
        'input',
        theme.utils
          .debounce((event) => {
            this.onChange(event);
          }, 300)
          .bind(this),
      );
    }
  }

  toggleResetButton() {}

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }
}

customElements.define('search-form', SearchForm);
class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.section = this.closest('[data-search-input]');
    this.search_drawer = document.querySelector('predictive-search-drawer');
    this.searchOpenDrawers = document.querySelectorAll('[data-predictive-search]');

    if (!Shopify.designMode) {
      this.search_drawer.addEventListener('focusout', this.handleBlur.bind(this));
    }

    this.setHeaderSearchIconAccessibility();
    this.cachedResults = {};
    this.predictiveSearchResults = this.section.querySelector('[data-predictive-search-result]');
    this.clearButton = this.querySelector('[data-reset-button]');
    this.searchButton = this.querySelector('[data-search-button]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Debounce configuration
    this.debounceTimer = null;
    this.debounceDelay = 300; // 300ms delay after typing stops

    this.setupEventListeners();
    this.showRecentSearches();

    if (this.search_drawer.querySelector('[data-swiper]')) {
      const element = this.search_drawer.querySelector('[data-swiper]');
      const properties = element.getAttribute('data-swiper');
      const recommendationsproducts = theme.SwiperSlide(element, JSON.parse(properties));
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.clearButton.addEventListener('click', this.onClearClick.bind(this));
  }

  handleBlur(event) {
    setTimeout(() => {
      if (this.search_drawer.classList.contains('open') && !this.search_drawer.contains(document.activeElement)) {
        if (this.search_drawer.querySelector('[data-drawer-close] button')) {
          this.search_drawer.querySelector('[data-drawer-close] button').focus();
        } else {
          this.querySelector('input').focus();
        }
      }
    }, 10);
  }

  getQuery() {
    const searchTerm = this.input.value.trim();
    this.storeSearchTerm(searchTerm);
    return searchTerm;
  }

  storeSearchTerm(searchTerm) {
    if (searchTerm.length > 0) {
      let recentSearches = this.getStoredSearchTerms();
      if (!recentSearches.includes(searchTerm)) {
        recentSearches.unshift(searchTerm);

        if (recentSearches.length > 7) {
          recentSearches.pop();
        }

        localStorage.setItem('recentSearchTerms', JSON.stringify(recentSearches));
      }
    }
  }

  getStoredSearchTerms() {
    const recentSearches = localStorage.getItem('recentSearchTerms');
    return recentSearches ? JSON.parse(recentSearches) : [];
  }

  showRecentSearches() {
    const recentSearches = this.getStoredSearchTerms();
    const recentSearch = document.querySelector('[data-recent-searches]');
    const recentSearchContainer = document.querySelector('[data-recent-searches] ul');

    if (recentSearches.length > 0 && recentSearchContainer) {
      recentSearch.classList.remove('hidden');
      const limitedSearches = recentSearches.slice(0, 7);

      limitedSearches.forEach((searchTerm, index) => {
        const searchItem = this.createSearchItem(searchTerm, index);
        recentSearchContainer.append(searchItem);
      });
    }
  }

  createSearchItem(searchTerm, index) {
    const searchItem = document.createElement('li');
    searchItem.classList.add('predictive-search-item');
    searchItem.id = `predictive-search-option-query-${index + 1}`;

    const link = document.createElement('a');
    link.href = `/search?q=${searchTerm}`;
    link.classList.add('predictive-search-item-link');

    const svgWrapper = this.createSvgWrapper();
    const textSpan = this.createTextSpan(searchTerm);

    link.appendChild(svgWrapper);
    link.appendChild(textSpan);
    searchItem.appendChild(link);

    return searchItem;
  }

  createSvgWrapper() {
    const svgWrapper = document.createElement('span');
    svgWrapper.classList.add('svg-wrapper');
    svgWrapper.innerHTML = `
      <svg viewBox="0 0 7 7" fill="none">
        <path d="M0.48249 0.923833L0.482511 1.77624L4.82887 1.77626L0.625632 5.77029L1.25997 6.37305L5.4632 2.37902L5.4632 6.50903L6.36025 6.50905L6.36025 0.923812L0.48249 0.923833Z" fill="currentColor"/>
      </svg>`;
    return svgWrapper;
  }

  createTextSpan(searchTerm) {
    const textSpan = document.createElement('span');
    textSpan.classList.add('predictive-search-link-text');
    textSpan.setAttribute('data-hover', searchTerm);
    textSpan.textContent = searchTerm;
    return textSpan;
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    // Clear previous debounce timer
    clearTimeout(this.debounceTimer);

    // Only trigger search if predictive search is enabled AND search term is not empty
    if (
      newSearchTerm.length > 0 &&
      document.querySelector('[data-enable-predictive-search]').getAttribute('data-enable-predictive-search') == 'true'
    ) {
      // Set new debounce timer
      this.debounceTimer = setTimeout(() => {
        this.getSearchResults(this.searchTerm);
      }, this.debounceDelay);
    } else if (newSearchTerm.length === 0) {
      // If input is empty, show recent searches
      this.onClearClick();
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
    this.toggleButtons();
  }

  onKeydown(event) {
    const query = this.getQuery();
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    if (this.predictiveSearchResults && query.length !== 0) {
      this.predictiveSearchResults.classList.remove('hidden');
      if (this.dataset?.source == 'header') {
        lockBodyScroll();
      }
      if (this.section.querySelector('[data-search-drawer-suggestions]')) {
        this.section.querySelector('[data-search-drawer-suggestions]').classList.add('hidden');
      }
    }
  }

  toggleButtons() {
    const query = this.getQuery();
    this.clearButton = this.querySelector('[data-reset-button]');
    this.searchButton = this.querySelector('[data-search-button]');

    if (query.length > 0) {
      this.clearButton.classList.remove('hidden');
      this.searchButton.classList.add('hidden');
    } else {
      this.clearButton.classList.add('hidden');
      this.searchButton.classList.remove('hidden');
    }
  }

  onClearClick(event = null) {
    if (event) event.preventDefault();
    this.clearButton.classList.add('hidden');
    this.searchButton.classList.remove('hidden');
    this.input.value = '';
    this.input.focus();
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add('hidden');
      if (this.dataset?.source == 'header') {
        unlockBodyScroll();
      }
      if (this.section.querySelector('[data-search-drawer-suggestions]')) {
        this.section.querySelector('[data-search-drawer-suggestions]').classList.remove('hidden');
      }
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          if (this.section.querySelector('[data-search-drawer-suggestions]')) {
            this.section.querySelector('[data-search-drawer-suggestions]').classList.remove('hidden');
          }
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.classList.add('hidden');
          }
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
        if (this.section.querySelector('[data-search-drawer-suggestions]')) {
          this.section.querySelector('[data-search-drawer-suggestions]').classList.add('hidden');
        }
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.classList.remove('hidden');
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.section.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.section.querySelector('[data-search-drawer-suggestions]')) {
      this.section.querySelector('[data-search-drawer-suggestions]').classList.add('hidden');
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.section.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  setHeaderSearchIconAccessibility() {
    if (!this.searchOpenDrawers || this.searchOpenDrawers.length === 0) return;

    this.searchOpenDrawers.forEach((searchOpenDrawer) => {
      searchOpenDrawer.addEventListener('click', (event) => {
        this.open(searchOpenDrawer);
      });
      searchOpenDrawer.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(searchOpenDrawer);
        }
      });
    });

    if (this.search_drawer) {
      this.drawerClose = this.search_drawer.querySelector('[data-drawer-close]');
      this.drawerClose.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });

      const drawerCloseButton = this.search_drawer.querySelector('predictive-search [data-drawer-close]');
      drawerCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    this.handleCursorMove();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (this.search_drawer) {
      this.search_drawer.classList.remove('open');
      unlockBodyScroll();

      setTimeout(() => {
        this.search_drawer.classList.add('open');
        lockBodyScroll();
        this.focusInputAfterTransition();
      }, 200);
    }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.search_drawer;
        const focusElement = this.search_drawer.querySelector('[data-drawer-close] button');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );
  }

  focusInputAfterTransition() {
    const input = this.search_drawer.querySelector('input');
    if (input) {
      setTimeout(() => {
        input.focus();
      }, 0);
    }
  }

  handleCursorMove() {
    if (!this.drawerClose) return;

    const cursorWrapper = this.section.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.drawerClose.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });

    this.drawerClose.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.drawerClose.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });
  }

  setFocusOnCloseButton() {
    const closeButton = this.search_drawer.querySelector('button');
    if (closeButton) {
      closeButton.focus();
    }
  }

  close() {
    this.search_drawer.classList.remove('open');
    removeTrapFocus(this.activeElement);
    unlockBodyScroll();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    super.disconnectedCallback();
  }
}

customElements.define('predictive-search', PredictiveSearch);

class MixAndMatchBundle extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.addParentToEachColumn('[bundle-product-wrapper-masonry]', '[bundle-product-item]');
    this.closeButton = this.querySelector('[data-bundle-close]') || this.querySelector('[data-bundle-overlay-close]');
    this.verticalContainer = this.querySelector('[bundle-product-wrapper-masonry]');
    this.leftSection = this.verticalContainer.querySelectorAll('.even-masonry');
    this.rightSection = this.verticalContainer.querySelectorAll('.odd-masonry');
    this.wrapperHeight = this.querySelector('[bundle-product-wrapper]');
    this.maxLimitBundleItem = parseInt(this.getAttribute('data-bundle-max-limit'));
    this.getAllBundleItem = this.querySelectorAll('[bundle-product-item]');
    this.getAllBundleItemCard = this.querySelectorAll('[bundle-product-item-card]');
    this.getAllBundleItemCounter = this.querySelectorAll('[bundle-product-select-count]');
    this.getAllBundleItemThumb = this.querySelectorAll('[bundle-thumb-product-item]');

    this.bundleProductThumbsMain = this.querySelector('[bundle-product-thumbs-main]');
    this.bundleProductSummaryWrapper = this.section.querySelector('[bundle-product-summary-wrapper]');
    this.buttonPopUpLayout = this.querySelector('[bundle-product-button-popup]');
    this.bundleProductCardWrap = this.querySelector('[bundle-product-card-wrap]');

    this.back_overlay = this.section.querySelector('[data-backdrop-overlay]');
    this.thumbProductMainBoundArea = this.bundleProductThumbsMain.getBoundingClientRect();
    this.boundarySummaryArea = this.bundleProductSummaryWrapper.getBoundingClientRect();
    this.topHeightClipPopUp = this.thumbProductMainBoundArea.top - this.boundarySummaryArea.top;
    this.bottomCordinatePopUp =
      this.thumbProductMainBoundArea.bottom - this.thumbProductMainBoundArea.height - this.boundarySummaryArea.bottom;
    this.leftCordinatePopUp = this.thumbProductMainBoundArea.left - this.boundarySummaryArea.left;
    this.rightCordinatePopUp = this.boundarySummaryArea.right - this.thumbProductMainBoundArea.right;
    document.querySelector('bundle-drawer').addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  connectedCallback() {
    this.initialize();
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const rect = this.getBoundingClientRect();
    const topShadow = this.section.querySelector('[data-top-shadow]');
    if (rect.top <= 0) {
      topShadow.classList.add('sticky');
    } else {
      topShadow.classList.remove('sticky');
    }
    if (rect.bottom <= window.innerHeight) {
      this.bundleProductCardWrap.classList.add('remove-height');
    } else {
      this.bundleProductCardWrap.classList.remove('remove-height');
    }
  }

  initialize() {
    // this.setStyleProperties();
    gsap.set(this.bundleProductCardWrap, {
      '--bundle-product-thumbs-height': `${this.thumbProductMainBoundArea.height}px`,
      '--bundle-product-thumbs-width': `${this.thumbProductMainBoundArea.width}px`,
    });

    this.getAllBundleItemCard.forEach((bundleItem, itemIndex) => {
      bundleItem.addEventListener('click', () => {
        this.handleBundleItemClick(bundleItem, itemIndex);
      });
      bundleItem.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          this.handleBundleItemClick(bundleItem, itemIndex);
        }
      });
    });

    this.buttonPopUpLayout.addEventListener('click', () => {
      this.handleButtonPopUpClick();
    });

    this.bindVariantImageEvents();
    this.handleCursorMove();
    this.addCloseListener();
  }

  bindVariantImageEvents() {
    this.getAllBundleItem.forEach((bundleItem, index) => {
      const variantSelect = bundleItem.querySelector('select');
      const productMedia = bundleItem.querySelector('[data-product-media]');
      const template = bundleItem.querySelector('template[data-type="variant-images"]');
      const extractedVariants = new Set(); // Track extracted variant images

      if (!variantSelect) return; // Exit if no select element is found

      // Function to handle the variant change and load behavior
      const handleVariantChange = (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const selectedVariantId = event.target.value; // Get selected variant ID
        const selectedVariantPricewithMoney = selectedOption.getAttribute('data-variant-price-with-money');
        const selectedVariantPrice = selectedOption.getAttribute('data-variant-price');
        const selectedVariantTitle = selectedOption.getAttribute('data-variant-title');

        // Extract images from template only if not already extracted
        if (template && !extractedVariants.has(selectedVariantId)) {
          const variantImages = template.content.querySelectorAll(`[data-variant-image]`);
          const fragment = document.createDocumentFragment();
          variantImages.forEach((node) => {
            if (!productMedia.querySelector(`[data-variant-image="${node.dataset.variantImage}"]`)) {
              fragment.appendChild(node.cloneNode(true));
              extractedVariants.add(node.dataset.variantImage); // Mark this variant as extracted
            }
          });

          productMedia.appendChild(fragment);
        }

        const variantImages = bundleItem.querySelectorAll('[data-variant-image]');
        const featuredImage = bundleItem.querySelector('[data-featured-image]');

        // Remove 'active' class from all variant images
        variantImages.forEach((image) => image.classList.remove('active'));

        // Add 'active' class to the selected variant image or fallback to the featured image
        const selectedImage = bundleItem.querySelector(`[data-variant-image="${selectedVariantId}"]`);
        if (selectedImage) {
          selectedImage.classList.add('active');
          bundleItem.querySelector('[data-bundle-cart-variant-image]').innerHTML = selectedImage.innerHTML;
        } else if (featuredImage) {
          featuredImage.classList.add('active');
          bundleItem.querySelector('[data-bundle-cart-variant-image]').innerHTML = featuredImage.innerHTML;
        }

        // Update variant data in the bundle cart
        bundleItem.querySelector('[name="id[]"]')?.setAttribute('value', selectedVariantId);
        const titleElement = bundleItem.querySelector('[data-bundle-cart-variant-title]');
        if (titleElement) titleElement.textContent = selectedVariantTitle;
        const priceElement = bundleItem.querySelector('[data-bundle-cart-variant-price]');
        if (priceElement) {
          priceElement.textContent = selectedVariantPricewithMoney;
          priceElement.setAttribute('data-bundle-cart-variant-price', selectedVariantPrice);
        }

        // Ensure correct bundle selection handling
        const allBundleItems = Array.from(this.querySelectorAll('[bundle-product-item-card]'));
        const activeBundleItem = bundleItem.querySelector('[bundle-product-item-card]');
        if (activeBundleItem) {
          const actualIndex = allBundleItems.indexOf(activeBundleItem);
          if (actualIndex !== -1) {
            activeBundleItem.classList.remove('active');
            this.handleBundleItemClick(activeBundleItem, actualIndex, true);
          }
        }

        this.renderBundleCart();
      };

      // Bind change event listener
      variantSelect.addEventListener('change', handleVariantChange);

      // Simulate the 'change' event on initial load (if the select has a default value)
      if (variantSelect.value) {
        // Trigger the event manually
        const simulatedEvent = new Event('change');
        variantSelect.dispatchEvent(simulatedEvent);
      }
    });
  }

  resetBundle() {
    const bundleCart = this.section.querySelector('bundle-cart');
    const bundleDrawer = this.section.querySelector('bundle-drawer');
    bundleDrawer.classList.remove('open');
    bundleCart.innerHTML = '';
    this.bundleProductCardWrap.classList.remove('open');
    const allThumbItems = this.querySelectorAll('.active[bundle-thumb-product-item]');
    allThumbItems.forEach((thumbItem) => {
      thumbItem.querySelector('span').innerText = '';
      let mediaElement = thumbItem.querySelector('img, svg');
      if (mediaElement) mediaElement.remove(); // Remove both img and svg
      thumbItem.classList.remove('active');
    });
    const activeItems = this.querySelectorAll('[bundle-product-item-card].active');
    activeItems.forEach((item, index) => {
      item.classList.remove('active');
    });
  }
  removeBundle() {
    // Remove existing event listeners to avoid duplication
    this.section.querySelectorAll('[data-bundle-remove]').forEach((removeButton) => {
      removeButton.removeEventListener('click', this.handleRemoveClick);
      removeButton.removeEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          this.handleRemoveClick.bind(this);
        }
      });
    });

    // Add new event listeners
    this.section.querySelectorAll('[data-bundle-remove]').forEach((removeButton) => {
      removeButton.addEventListener('click', this.handleRemoveClick.bind(this));
      removeButton.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          this.handleRemoveClick.call(this, event);
        }
      });
    });
  }

  handleRemoveClick(event) {
    event.stopPropagation(); // Stop the event from bubbling up to the document
    const removeButton = event.currentTarget;
    const bundleItem = removeButton.closest('bundle-cart-item');
    const buttonIndex = bundleItem.getAttribute('data-index');
    // Get all bundle product items
    const allBundleItems = Array.from(this.querySelectorAll('[bundle-product-item-card]'));
    const activeBundleItems = Array.from(this.querySelectorAll('[bundle-product-item-card].active'));
    // Find the corresponding active bundle item card
    const activeBundleItem = activeBundleItems[buttonIndex];
    if (activeBundleItem) {
      // Find the correct index of the active item in the full list
      const actualIndex = allBundleItems.indexOf(activeBundleItem);
      if (actualIndex !== -1) {
        activeBundleItem.classList.remove('active');
        this.handleBundleItemClick(activeBundleItem, actualIndex, true);
      }
    }
    bundleItem.remove();
    const bundleCart = this.section.querySelectorAll('bundle-cart');
    const bundleItems = this.section.querySelectorAll('bundle-cart bundle-cart-item');
    if (bundleItems.length >= this.dataset.bundleMinLimit) {
      this.section.querySelector('[data-bundle-submit]').classList.remove('hidden');
    } else {
      this.section.querySelector('[data-bundle-submit]').classList.add('hidden');
    }
  }

  renderBundleCart() {
    // const bundleCart = this.querySelector('bundle-cart');
    const bundleCart = this.section.querySelector('bundle-cart');
    const activeItems = this.querySelectorAll('[bundle-product-item-card].active');
    this.section.querySelector('[data-bundle-submit]').classList.remove('hidden');
    bundleCart.innerHTML = ''; // Clear existing content
    activeItems.forEach((item, index) => {
      const cartItem = item.querySelector('bundle-cart-item');
      if (cartItem) {
        const clonedItem = cartItem.cloneNode(true); // Clone the element
        clonedItem.classList.remove('hidden'); // Remove 'hidden' class
        clonedItem.setAttribute('data-index', index);
        bundleCart.appendChild(clonedItem); // Append to <bundle-cart>
      }
    });
    this.removeBundle();
    this.handleSumOfBundle();
  }

  handleSumOfBundle() {
    const bundleCart = this.section.querySelectorAll('bundle-cart');
    const bundleItems = this.section.querySelectorAll('bundle-cart bundle-cart-item');
    if (bundleItems.length > 0) {
      this.bundleProductCardWrap.classList.add('open');
    } else {
      this.bundleProductCardWrap.classList.remove('open');
    }
    if (bundleItems.length >= this.dataset.bundleMinLimit) {
      this.buttonPopUpLayout.classList.remove('hidden');
      this.buttonPopUpLayout.removeAttribute('disabled');

      this.buttonPopUpLayout.classList.add('show');
    } else {
      this.buttonPopUpLayout.classList.add('hidden');
      this.buttonPopUpLayout.setAttribute('disabled', 'disabled');

      this.buttonPopUpLayout.classList.remove('show');
    }

    setTimeout(() => {
      gsap.set(this.bundleProductCardWrap, {
        '--bundle-product-thumbs-height': `${this.thumbProductMainBoundArea.height}px`,
        '--bundle-product-thumbs-width': `${this.thumbProductMainBoundArea.width}px`,
      });
    }, 100);

    let total = 0.0;
    bundleItems.forEach((item) => {
      const price = parseFloat(
        item.querySelector('[data-bundle-cart-variant-price]').getAttribute('data-bundle-cart-variant-price'),
      );
      total += price;
    });
    this.section.querySelector('[data-bundle-cart-total]').textContent = theme.Currency.formatMoney(
      total,
      theme.settings.moneyFormat,
    );
    if (total <= 0) {
      this.close();
    }
  }

  handleBundleItemClick(bundleItem, itemIndex, forceRemove = false) {
    if (bundleItem.classList.contains('sold-out')) return;
    let activeItems = this.querySelectorAll('.active[bundle-product-item-card]');
    // If forceRemove is true, we should only remove the item (avoid toggling)
    if (forceRemove || bundleItem.classList.contains('active')) {
      bundleItem.classList.remove('active');
      let currentTextCountNo = this.getAllBundleItemCounter[itemIndex].innerText;
      this.getAllBundleItemCounter[itemIndex].innerText = '';
      this.manageBundleItemCount(currentTextCountNo, activeItems.length);
      this.addDisableAttributeBundleItem(this.maxLimitBundleItem, activeItems.length - 1);
      this.manageBundleThumbProduct(itemIndex, activeItems.length, currentTextCountNo, 'removeActive');
    } else {
      bundleItem.classList.add('active');
      this.getAllBundleItemCounter[itemIndex].innerText = activeItems.length + 1;
      this.getAllBundleItemCounter[itemIndex].setAttribute('bundle-product-select-count', activeItems.length + 1);
      this.addDisableAttributeBundleItem(this.maxLimitBundleItem, activeItems.length + 1);
      this.manageBundleThumbProduct(itemIndex, activeItems.length, activeItems.length + 1, 'addActive');
    }
    this.renderBundleCart();
  }

  close() {
    removeTrapFocus(this.buttonPopUpLayout.querySelector('button'));
    let getActiveListProduct = this.querySelectorAll('.active[bundle-product-item-card]');
    if (getActiveListProduct.length >= this.dataset.bundleMinLimit) {
      if (!this.buttonPopUpLayout.classList.contains('show')) {
        this.buttonPopUpLayout.classList.add('show');
      }
    }
    this.bundleProductCardWrap.classList.remove('active');
    this.section.querySelector('bundle-drawer').classList.remove('open');
    unlockBodyScroll();
  }

  addCloseListener() {
    this.section.querySelector('bundle-drawer').addEventListener('click', (e) => {
      if (e.target.closest('[data-backdrop-overlay]')) {
        this.close();
      }
    });
  }

  handleCursorMove() {
    if (!this.back_overlay) return;

    const cursorWrapper = this.section.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.back_overlay.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });

    this.back_overlay.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.back_overlay.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });
  }

  handleButtonPopUpClick() {
    this.bundleProductCardWrap.classList.add('active');

    if (this.buttonPopUpLayout.classList.contains('show')) {
      this.buttonPopUpLayout.classList.remove('show');
    }

    this.section.querySelector('bundle-drawer').classList.add('open');
    trapFocus(
      this.section.querySelector('bundle-drawer'),
      this.section.querySelector('bundle-drawer').querySelector('bundle-cart a'),
    );
    lockBodyScroll();
  }

  addParentToEachColumn(columnParent, columnItems) {
    const containerMasonry = this.querySelector(columnParent);
    const itemsMasonry = Array.from(this.querySelectorAll(columnItems)); // Safari fix: Convert NodeList to Array

    if (!containerMasonry || itemsMasonry.length === 0) return;

    // Use getBoundingClientRect() for reliable width (Safari fix)
    const itemWidth = itemsMasonry[0].getBoundingClientRect().width;
    const containerWidth = containerMasonry.getBoundingClientRect().width;
    const columnCount = Math.max(1, Math.floor(containerWidth / itemWidth)); // Ensure at least 1 column

    containerMasonry.innerHTML = ''; // Clear existing content

    // Create columns
    const columns = Array.from({ length: columnCount }, () => {
      const column = document.createElement('div');
      column.classList.add('masonry-column');
      containerMasonry.appendChild(column);
      return column;
    });

    // Distribute items
    itemsMasonry.forEach((item, index) => {
      const columnIndex = index % columnCount;
      columns[columnIndex].appendChild(item);
    });

    // Add even/odd classes (optional, for styling/animations)
    columns.forEach((column, index) => {
      column.classList.add(index % 2 === 0 ? 'even-masonry' : 'odd-masonry');
    });

    return columns; // Return columns for later use in animation
  }

  addDisableAttributeBundleItem(maxBundleLimit, currentActiveItem) {
    let allNonActiveBundleItem = this.querySelectorAll('[bundle-product-item-card]:not(.active)');
    if (maxBundleLimit > currentActiveItem) {
      // this.bundleProductCartButton.classList.remove("show")
      allNonActiveBundleItem.forEach((elementBundleItem) => {
        elementBundleItem.removeAttribute('disabled');
      });
    } else {
      // this.bundleProductCartButton.classList.add("show")
      allNonActiveBundleItem.forEach((elementBundleItem) => {
        elementBundleItem.setAttribute('disabled', '');
      });
    }
  }

  manageBundleItemCount(countCurrent, totalActive) {
    let numberValue = parseInt(countCurrent);
    let getActiveBundleItemSpan = Array.from(
      this.querySelectorAll('.active[bundle-product-item-card] [bundle-product-select-count]'),
    );
    let sortedActiveElements = getActiveBundleItemSpan.sort(
      (a, b) => parseInt(a.innerText, 10) - parseInt(b.innerText, 10),
    );
    sortedActiveElements.forEach((spanElementCountD, keyIndex) => {
      spanElementCountD.setAttribute('bundle-product-select-count', keyIndex + 1);
      spanElementCountD.innerText = keyIndex + 1;
    });
  }

  manageBundleThumbProduct(clickIndex, activeC, countNo, actionClick) {
    let numberCountClick = parseInt(countNo);
    if (actionClick === 'addActive') {
      let getAllInactiveThumbs = this.querySelectorAll('[bundle-thumb-product-item]:not(.active)');

      if (getAllInactiveThumbs.length === 0) return; // Prevent errors if no inactive elements exist

      let bundleItem = this.getAllBundleItem[clickIndex];

      if (!bundleItem) return; // Prevent errors if index is out of range

      let activeVariantImage = bundleItem.querySelector(
        '[data-variant-image].active img, [data-variant-image].active svg',
      );

      if (!activeVariantImage) return; // Prevent errors if no active variant image exists

      let imageClone = activeVariantImage.cloneNode(true); // Clone the element (img or svg)

      getAllInactiveThumbs[0].classList.add('active');
      getAllInactiveThumbs[0].querySelector('span').innerText = countNo;

      let firstChild = getAllInactiveThumbs[0].firstElementChild;
      if (firstChild) {
        firstChild.appendChild(imageClone);
      }
    } else {
      let activeThumbs = this.querySelectorAll('.active[bundle-thumb-product-item]');
      let allThumbItems = this.querySelectorAll('.active[bundle-thumb-product-item]');
      let activeBundleItems = Array.from(
        this.querySelectorAll('.active[bundle-product-item-card] [bundle-product-select-count]'),
      );

      let sortedActiveElements = activeBundleItems.sort(
        (a, b) => parseInt(a.innerText, 10) - parseInt(b.innerText, 10),
      );

      allThumbItems.forEach((thumbItem) => {
        thumbItem.querySelector('span').innerText = '';
        let mediaElement = thumbItem.querySelector('img, svg');
        if (mediaElement) mediaElement.remove(); // Remove both img and svg
        thumbItem.classList.remove('active');
      });

      sortedActiveElements.forEach((thumbElement, index) => {
        let mediaElement = thumbElement
          .closest('[bundle-product-item-card]')
          .querySelector('[data-variant-image].active img, [data-variant-image].active svg');

        if (!mediaElement) return;

        let clonedMedia = mediaElement.cloneNode(true); // Clone the image or SVG

        allThumbItems[index].classList.add('active');
        allThumbItems[index].querySelector('span').innerText = index + 1;
        allThumbItems[index].firstElementChild.appendChild(clonedMedia);
      });
    }
  }
}

customElements.define('mix-and-match-bundle', MixAndMatchBundle);

class BuildKit extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.hotspots = this.querySelectorAll('[data-hot-spot]');
    this.kits = this.querySelector('kit-context');
    this.kitProducts = this.querySelectorAll('[data-product]');
    this.variantSelects = this.querySelectorAll('[data-select-control] select');
    this.toggle = this.querySelector('[data-kit-toggle]');
    this.viewKit = this.querySelector('[data-view-kit-button]');
  }

  connectedCallback() {
    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener('click', () => this.handleHotspotClick(hotspot));

      hotspot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.handleHotspotClick(hotspot);
        }
      });
    });

    if (this.dataset.mode === 'spot-and-shop') {
      this.viewKit.addEventListener('click', () => this.toggleKitBox());
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', () => this.updateButtonPrice());
      });

      this.toggle.addEventListener('click', () => this.toggleKitBox());

      const productForm = this.querySelector('[is="product-form"]');
      productForm.style.setProperty('--kit-context-height', `${productForm.getBoundingClientRect().height}px`);
    }

    if (this.kits) {
      // Mobile-only event handling for `mouseenter` & `mouseleave`
      const handleKitMouseEnter = () => {
        lockBodyScroll();
      };

      const handleKitMouseLeave = () => {
        unlockBodyScroll();
      };

      const updateKitEventListeners = () => {
        if (window.innerWidth <= 767) {
          this.kits.removeEventListener('mouseenter', handleKitMouseEnter);
          this.kits.removeEventListener('mouseleave', handleKitMouseLeave);
        } else {
          this.kits.addEventListener('mouseenter', handleKitMouseEnter);
          this.kits.addEventListener('mouseleave', handleKitMouseLeave);
        }
      };

      // Initial check to attach/detach event listeners based on screen size
      updateKitEventListeners();

      // Listen for window resize to dynamically update event listeners
      window.addEventListener('resize', updateKitEventListeners);
    }
    this.productFlagPosition();
  }

  productFlagPosition() {
    const productFlag = this.querySelectorAll('[data-product-flag]');
    productFlag.forEach((flag) => {
      const productFlagPosition = flag.getBoundingClientRect();
      if (productFlagPosition.right >= window.innerWidth) {
        flag.classList.add('open-flag-left');
      }
    });
  }

  toggleKitBox() {
    const productForm = this.querySelector('[is="product-form"]');
    if (productForm.classList.contains('collapse')) {
      productForm.classList.replace('collapse', 'expand');
      this.viewKit.classList.add('hidden');
      this.querySelector('[data-kit-button]').classList.remove('hidden');
    } else {
      productForm.classList.replace('expand', 'collapse');
      this.viewKit.classList.remove('hidden');
      this.querySelector('[data-kit-button]').classList.add('hidden');
    }
  }

  calculateTotalPrice() {
    let totalPrice = 0.0;
    const activeKitProducts = this.querySelectorAll('[data-product].active');
    activeKitProducts.forEach((product) => {
      const select = product.querySelector('[data-select-control] select');
      if (!select) {
        console.warn('Select element not found in product:', product);
        return;
      }
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) {
        console.warn('Selected option not found in select:', select);
        return;
      }
      const price = parseFloat(selectedOption.dataset.variantPrice || 0.0);
      if (isNaN(price)) {
        console.warn('Invalid price found for selected option:', selectedOption);
        return;
      }
      totalPrice += price;
    });
    return totalPrice;
  }

  handleHotspotClick(hotspot) {
    const hotSpotSource = hotspot.dataset.hotSpot;
    const product = this.querySelector(`[data-product="kit-item-${hotSpotSource}"]`);

    if (product) {
      const isActive = product.classList.contains('active');
      if (isActive) {
        product.classList.remove('active');
        hotspot.classList.remove('active');
      } else {
        product.classList.add('active');
        hotspot.classList.add('active');
      }

      const productForm = this.querySelector('[is="product-form"]');
      if (productForm && productForm.classList.contains('collapse')) {
        productForm.classList.replace('collapse', 'expand');
        this.viewKit.classList.add('hidden');
        this.querySelector('[data-kit-button]').classList.remove('hidden');
      }
    }

    if (this.dataset.mode === 'spot-and-shop') {
      this.updateButtonPrice();
    }
  }

  updateButtonPrice() {
    const totalPrice = this.calculateTotalPrice();
    const formattedPrice = theme.Currency.formatMoney(totalPrice, theme.settings.moneyFormat);
    const button = this.querySelector('[data-kit-button]');
    this.querySelector('[data-kit-price]').textContent = formattedPrice;
    this.variantSelects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      const hiddenInput = select.closest('[data-select-control]').querySelector('input[type="hidden"]');
      if (hiddenInput) {
        hiddenInput.value = selectedOption.value; // Update hidden input value
      }
    });
    if (totalPrice > 0) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', 'disabled');
    }
  }
}
customElements.define('build-kit', BuildKit);

class NewsletterModal extends HTMLElement {
  constructor() {
    super();

    this.section = this.closest('.shopify-section');
    this.newsletterModal = this.section?.querySelector('[data-product-newsletter-content]');
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close()); // code added by neha
    this.template = this.newsletterModal?.querySelector('template');

    if (!this.section || !this.newsletterModal || !this.template) {
      console.warn('NewsletterModal: Required elements missing.');
      return;
    }

    if (Shopify.designMode) {
      this.section.addEventListener('shopify:section:select', () => this.open());
    }

    if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) {
      return;
    }

    this.init();
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get shouldLock() {
    return true;
  }

  get testMode() {
    return this.dataset.testMode === 'true';
  }

  get delay() {
    return parseInt(this.dataset.delay) || 5;
  }

  get expiry() {
    return parseInt(this.dataset.expiry) || 30;
  }

  get cookieName() {
    return 'newsletter-popup';
  }

  get submited() {
    if (localStorage.getItem('theme:popup-filled') === 'true') {
      this.setCookie('newsletter-popup', 'true', this.expiry);
    }
    return this.querySelector('.alert') !== null;
  }

  get visitor() {
    return this.dataset.visitor;
  }

  init() {
    const get_cookie_value = this.getCookie(this.cookieName);

    if (this.submited) {
      this.load(1);
      return;
    }
    if (get_cookie_value !== 'newsletter-popup' && window.location.pathname.indexOf('/challenge') < 0) {
      localStorage.setItem('theme:popup-filled', 'false');
    }
    if (Shopify.designMode) {
      if (this.dataset.testMode == 'true' || this.getCookie(this.cookieName) != 'true') {
        this.load(this.delay);
      }
    }

    if (Shopify && this.dataset.testMode == 'false') {
      if (this.getCookie(this.cookieName) != 'true') {
        this.load(this.delay);
      }
    }
  }

  load(delay) {
    if (Shopify && Shopify.designMode) return;
    setTimeout(() => this.open(), delay * 1000);
  }

  open() {
    const content = this.template.content.cloneNode(true);
    this.appendChild(content);

    this.popup = document.querySelector('newsletter-modal');

    if (!this.popup) {
      console.error('NewsletterModal: Popup not found after insertion.');
      return;
    }

    this.back_overlay = this.popup.querySelector('[data-backdrop-overlay]');
    if (!this.back_overlay) {
      console.warn('NewsletterModal: [data-backdrop-overlay] is missing.');
    }

    this.popup.classList.add('open');
    lockBodyScroll();

    // code added by neha
    const containerToTrapFocusOn = this.popup;
    const focusElement = this.popup.querySelector('input');
    trapFocus(containerToTrapFocusOn, focusElement);
    this.addCloseListener();
    this.handleCursorMove();
  }

  handleCursorMove() {
    if (!this.back_overlay) return;

    const cursorWrapper = this.popup.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.back_overlay.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });

    this.back_overlay.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.back_overlay.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });
  }

  close() {
    this.setCookie(this.cookieName, this.expiry);
    if (this.testMode || this.visitor == 'false') {
      this.removeCookie(this.cookieName);
    }
    if (!this.popup) return;

    this.popup.classList.remove('open');
    unlockBodyScroll();
    this.popup.addEventListener('transitionend', () => this.popup.remove(), { once: true });
  }

  addCloseListener() {
    if (!this.popup) return;

    this.popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-backdrop-overlay]') || e.target === this.popup) {
        this.close();
      }
    });
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/`;
  }
}
customElements.define('newsletter-modal', NewsletterModal);

class AgeVerifier extends HTMLElement {
  constructor() {
    super();

    if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) {
      return;
    }
    this.init();

    if (Shopify.designMode) {
      this.section = this.closest('.shopify-section');
      this.section.addEventListener('shopify:section:select', () => this.open());
    }
  }

  get shouldLock() {
    return true;
  }

  get testMode() {
    return this.dataset.testMode === 'true';
  }

  get delay() {
    return 1;
  }

  get expiry() {
    return parseInt(this.dataset.expiry) || 30;
  }

  get cookieName() {
    return 'age-verifier';
  }

  init() {
    if (Shopify.designMode) {
      if (this.dataset.testMode == 'true' || this.getCookie(this.cookieName) != 'true') {
        this.load(this.delay);
      }
    }

    if (Shopify && this.dataset.testMode == 'false') {
      if (this.getCookie(this.cookieName) != 'true') {
        this.load(this.delay);
      }
    }
  }

  load(delay) {
    if (Shopify && Shopify.designMode) return;
    setTimeout(() => this.open(), delay * 1000);
  }
  open() {
    setTimeout(() => {
      this.classList.add('open');
      lockBodyScroll();
      this.addCloseListener();
      // code added by neha
      trapFocus(this, this.querySelector('button'));
    }, 2000);
  }
  close() {
    this.setCookie(this.cookieName, this.expiry);
    this.classList.remove('open');
    unlockBodyScroll();

    this.addEventListener(
      'transitionend',
      () => {
        this.remove();
      },
      { once: true },
    );
  }
  addCloseListener() {
    this.addEventListener('click', (e) => {
      const isCloseButton = e.target.closest('[data-age-submit]');
      if (isCloseButton || e.target === this.popup) {
        this.close();
      }
    });
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0`;
  }
}
customElements.define('age-verification', AgeVerifier);

class QuickViewDrawer extends HTMLElement {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.section = this.closest('.shopify-section');

      this.section.addEventListener('shopify:section:select', () => this.open());
      this.section.addEventListener('shopify:section:deselect', () => this.close());
    }
  }
  open() {
    this.classList.add('open');
    lockBodyScroll();
  }
  close() {
    this.classList.remove('open');
    unlockBodyScroll();
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickView extends HTMLButtonElement {
  constructor() {
    super();
    this.section = this.closest('section');

    this.init();
    if (Shopify.designMode) {
      this.addEventListener('shopify:section:select', (event) => console.log(event));
    }
  }
  setActiveElement(element) {
    this.activeElement = element;
  }

  init() {
    if (!this.section) {
      return;
    }
    this.productCard = this.section.querySelector('[data-product-card]');
    this.quickViewElement = this.productCard.querySelector('[data-quick-view]');
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.height = this.quickViewElement?.offsetHeight;

    this.productCardlist = this.section.querySelectorAll('[data-product-card]');
    if (!this.productCardlist.length) return;

    this.productCardlist.forEach((item) => {
      this.hoverOption = item.querySelector('[data-hover-option]');
      if (this.hoverOption) {
        this.hoverOption.style.setProperty('--quickview-height', `${this.height}px`);
      }
    });

    this.selector = '[data-drawer="quick-view-drawer"]';

    this.drawer = document.querySelector(this.selector);
    if (!this.drawer) return;
    this.drawerContent = this.drawer.querySelector('[data-quick-view-content]');

    this.data_close_drawer = this.drawer.querySelector('[data-drawer-close]');

    this.addEventListener('click', this.onClick.bind(this));
    this.drawer.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close()); //code added by neha
    if (this.data_close_drawer) {
      this.data_close_drawer.addEventListener('click', () => {
        this.close();
      });
    }

    this.backdropClose = this.drawer.querySelector('[data-backdrop-overlay]');
    this.backdropClose.addEventListener('click', () => {
      this.close();
    });

    this.handleCursorMove();
  }
  handleCursorMove() {
    const cursorWrapper = this.drawer.querySelector('[data-cursor-hover]');

    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    this.backdropClose.addEventListener('mousemove', (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    });
    this.backdropClose.addEventListener('mouseleave', (e) => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });
    this.backdropClose.addEventListener('mouseenter', (e) => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });
  }

  addEventListeners() {
    this.addEventListener('mouseenter', (e) => this.handleMouseEnter(e));
    this.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
  }

  onClick(event) {
    event.preventDefault();
    this.fetchProduct();
    if (event.target) this.setActiveElement(event.target);
  }

  open() {
    if (this.drawer) {
      setTimeout(() => {
        this.drawer.classList.add('open');
        lockBodyScroll();
      }, 200);
      setTimeout(() => {
        this.drawer.classList.add('shadow');
      }, 600);
      setTimeout(() => {
        const containerToTrapFocusOn = this.drawer;
        const focusElement = this.drawer.querySelector('product-info variant-selects li input');
        trapFocus(containerToTrapFocusOn, focusElement);
      }, 800);
      this.drawerContent.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        (Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        }),
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML)),
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag));
      });
    }
  }

  close() {
    if (this.drawer) {
      this.drawer.classList.remove('open');
      unlockBodyScroll();
      this.drawer.classList.remove('shadow');
      // removeTrapFocus(this.activeElement);
    }
  }

  fetchProduct() {
    const sectionId = this.drawer.dataset.sectionId;
    const sectionUrl = `${this.dataset.productUrl}?section_id=${sectionId}`;

    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const productElement = new DOMParser().parseFromString(responseText, 'text/html').querySelector(this.selector);

        if (productElement) {
          this.drawerContent.innerHTML = productElement.querySelector('[data-quick-view-content]').innerHTML;
          this.open();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        } else {
          console.info('Product element not found in the fetched content.');
        }
      })
      .catch((error) => {
        console.info('Error fetching or parsing product content:', error);
      });
  }
}
customElements.define('quick-view', QuickView, { extends: 'button' });

class FeaturedCollectionTab extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.setInitialActiveTab(); // Always set the initial active tab
    this.handleResize(); // Adjust listeners based on current width
    window.addEventListener('resize', () => this.handleResize());
  }

  init() {
    this.mainWrapper = this.querySelector('[featured-collections-main-wrapper]');
    this.childTabs = this.querySelectorAll('[feature-collection-tab-child]');
    this.leftHeadings = this.querySelectorAll('[feature-collection-list-left-heading]');
    this.tabContentSections = this.querySelectorAll('[feature-list-tabs-content-sec]');
  }

  handleActiveClass() {
    this.childTabs.forEach((tabItem, tabIndex) => {
      const tabClickHandler = () => {
        if (!tabItem.classList.contains('active-tab')) {
          this.removeActiveClass(this.childTabs, this.tabContentSections, this.leftHeadings);
          tabItem.classList.add('active-tab');
          this.tabContentSections[tabIndex].classList.add('active');
          this.leftHeadings[tabIndex].classList.add('fade-in');
        }
      };

      tabItem.addEventListener('click', tabClickHandler);
      tabItem._clickHandler = tabClickHandler;
    });
  }

  removeActiveClass(tabItems, contentItems, rightContentItems) {
    tabItems.forEach((tab, index) => {
      tab.classList.remove('active-tab');
      contentItems[index].classList.remove('active');
      rightContentItems[index].classList.remove('fade-in');
    });
  }

  setInitialActiveTab() {
    if (this.childTabs.length > 0) {
      this.childTabs[0].classList.add('active-tab');
      this.tabContentSections[0].classList.add('active');
      this.leftHeadings[0].classList.add('fade-in');
    }
  }

  addEventListeners() {
    this.removeEventListeners(); // Just to be safe — avoid dupes
    this.handleActiveClass();
  }

  removeEventListeners() {
    this.childTabs.forEach((tabItem) => {
      if (tabItem._clickHandler) {
        tabItem.removeEventListener('click', tabItem._clickHandler);
        delete tabItem._clickHandler;
      }
    });
  }

  handleResize() {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1500) {
      this.addEventListeners();
    } else {
      this.removeEventListeners();
    }
  }
}

customElements.define('featured-collection-tabs', FeaturedCollectionTab);

class DeviceSwipe extends HTMLDivElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.isMobile = window.innerWidth <= 768;

    this.handleResize = this.handleResize.bind(this);
    this.handleLoad = this.handleLoad.bind(this);
  }

  connectedCallback() {
    // Listen to resize
    window.addEventListener('resize', this.handleResize);

    // DOMContentLoaded might already be fired, so check:
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      this.handleLoad();
    } else {
      document.addEventListener('DOMContentLoaded', this.handleLoad);
    }

    // Just in case it's still not rendered properly
    setTimeout(() => this.handleResize(), 100);
  }

  handleLoad() {
    this.initSwiper();
  }

  initSwiper() {
    this.isMobile = window.innerWidth <= 768;

    if (this.isMobile && !this.swiperInstance) {
      this.swiperInstance = new Swiper(this, {
        slidesPerView: 1,
        spaceBetween: 10,
        pagination: {
          el: `.swiper-pagination-${this.dataset.sectionId}`,
          clickable: true,
        },
      });
    }
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  handleResize() {
    const isNowMobile = window.innerWidth <= 768;

    if (isNowMobile && !this.swiperInstance) {
      this.initSwiper();
    } else if (!isNowMobile && this.swiperInstance) {
      this.destroySwiper();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('DOMContentLoaded', this.handleLoad);
  }
}

customElements.define('device-swipe', DeviceSwipe, { extends: 'div' });

class LookBookTabs extends HTMLUListElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.initTabs();
    this.initCustomCursor();
  }

  initTabs() {
    const tabs = this.querySelectorAll('[data-tab]');
    const product_body = this.section.querySelector('[data-product-body]');
    const tabContents = this.section.querySelectorAll('[data-tab-content]');

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        // Remove active classes from all tabs and contents
        tabs.forEach((t) => t.classList.remove('active'));
        tabContents.forEach((content) => content.classList.remove('active'));

        // Add active class to the clicked tab and corresponding content
        tab.classList.add('active');
        const content = tabContents[index];
        if (content) {
          content.classList.add('active');
        }

        // Toggle the 'details-open' class on the body
        if (!product_body.classList.contains('details-open')) {
          product_body.classList.add('details-open');
        }
      });
    });
  }

  initCustomCursor() {
    const product_body = this.section.querySelector('[data-product-body]');
    const closeCursor = this.section.querySelector('[data-ref="cursor"]');

    if (!closeCursor || !product_body) return;

    // Hide the default cursor and show the custom cursor

    closeCursor.style.position = 'absolute';
    closeCursor.style.pointerEvents = 'none';
    closeCursor.style.opacity = 0;
    product_body.style.cursor = 'unset';
    // Track mouse movement
    product_body.addEventListener('mousemove', (event) => {
      if (product_body.classList.contains('details-open')) {
        product_body.style.cursor = 'none';
        closeCursor.style.opacity = 1; // Show custom cursor

        // Get the bounds of the body
        const rect = product_body.getBoundingClientRect();
        let cursorX = event.clientX;
        let cursorY = event.clientY;
        // Clamp the cursor position within the body bounds
        cursorX = cursorX - rect.left;
        cursorY = cursorY - rect.top;
        closeCursor.style.transform = `translate3d(${cursorX}px, ${cursorY}px, 0)`;
      } else {
        closeCursor.style.opacity = 0; // Hide custom cursor
        product_body.style.cursor = 'unset';
      }
    });

    // Close on click
    product_body.addEventListener('click', (event) => {
      if (product_body.classList.contains('details-open')) {
        const rect = product_body.getBoundingClientRect();
        const isInsideContent =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (isInsideContent) {
          product_body.classList.remove('details-open');
          closeCursor.style.opacity = 0; // Hide the cursor
          product_body.style.cursor = 'unset';
        }
      }
    });
  }
}

customElements.define('lookbook-tabs', LookBookTabs, { extends: 'ul' });

class LightboxViewer extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('section');
    this.mediaItem = this.closest('[data-media-item]');
    this.sectionId = this.dataset.section;
    this.productMediaContent = this.section?.querySelector('[data-product-media-content]');

    if (this.productMediaContent) {
      this.template = this.productMediaContent.querySelector('template');
      this.handleClick = this.openMediaLightBoxViewer.bind(this);
      this.handleKeydown = this.handleEscapeKey.bind(this);
      this.addEventListener('click', this.handleClick);
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape') {
      this.closeMediaLightBoxViewer();
    }
  }

  openMediaLightBoxViewer() {
    if (!this.template) return;
    const content = this.template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(content);

    this.popup = document.querySelector(`#MediaLightboxDrawer-${this.sectionId}`);
    this.back_overlay = this.popup.querySelector('[data-backdrop-overlay]');

    if (!this.popup) return;

    const mediaGallery = this.popup.querySelector('media-gallery');
    if (mediaGallery) {
      mediaGallery.jumpToSlide(parseInt(this.mediaItem.dataset.index, 10));
    }

    requestAnimationFrame(() => {
      this.popup.classList.add('open');
      document.body.classList.add('overflow-hidden');
      document.body.setAttribute('data-lenis-prevent', 'true');
    });

    this.popup.addEventListener('click', (e) => {
      const isCloseButton = e.target.closest('[data-drawer-close]');
      if (isCloseButton || e.target === this.popup) {
        this.closeMediaLightBoxViewer();
      }
    });

    document.addEventListener('keydown', this.handleKeydown);

    this.handleCursorMove();
  }

  handleCursorMove() {
    if (!this.back_overlay) return;

    const cursorWrapper = this.popup.querySelector('[data-cursor-hover]');
    if (!cursorWrapper) return;
    gsap.set(cursorWrapper, { xPercent: 0, yPercent: 0 });

    const xTo = gsap.quickTo(cursorWrapper, 'x', { duration: 0.1, ease: 'power3' });
    const yTo = gsap.quickTo(cursorWrapper, 'y', { duration: 0.1, ease: 'power3' });

    // Ensure you only add the event listener once
    const onMouseMove = (e) => {
      gsap.to(cursorWrapper, { opacity: 1, scale: 1, duration: 0.1 });
      xTo(e.clientX);
      yTo(e.clientY);
    };

    // Add event listeners if they haven't been added before
    this.back_overlay.addEventListener('mousemove', onMouseMove);

    this.back_overlay.addEventListener('mouseleave', () => {
      gsap.to(cursorWrapper, { scale: 0, duration: 0.1 });
    });

    this.back_overlay.addEventListener('mouseenter', () => {
      gsap.to(cursorWrapper, { scale: 1, duration: 0.1 });
    });

    // Cleanup: Remove the mousemove event listener when it's no longer needed
    // (optional, if needed for stopping the effect on some condition)
    // this.back_overlay.removeEventListener('mousemove', onMouseMove);
  }

  closeMediaLightBoxViewer() {
    if (!this.popup) return;

    this.popup.classList.remove('open');
    document.body.classList.remove('overflow-hidden');
    document.body.removeAttribute('data-lenis-prevent');

    document.removeEventListener('keydown', this.handleKeydown);

    this.popup.addEventListener(
      'transitionend',
      () => {
        this.popup.remove();
        this.popup = null;
      },
      { once: true },
    );
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }
}

customElements.define('lightbox-viewer', LightboxViewer);

class MediaLightBoxViewerModal extends HTMLElement {
  constructor() {
    super();

    this.section = this.closest('section');
    this.mediaGallery = this.querySelector('media-gallery');
    this.controlToggle = this.querySelector('[data-action="toggle-menu"]');
    this.menuIcon = this.querySelector('[data-menu]');
    this.closeIcon = this.querySelector('[data-close]');
    this.controlsList = this.querySelector('[data-controls-list]');
    // Get all control buttons
    this.moveButtons = this.controlsList?.querySelectorAll('[data-action^="move"]');
    this.zoomInButton = this.controlsList?.querySelector('[data-action="zoom-in"]');
    this.zoomOutButton = this.controlsList?.querySelector('[data-action="zoom-out"]');

    // Store transform values
    this.transformValues = { x: 0, y: 0, scale: 1 };
  }

  connectedCallback() {
    this.controlToggle?.addEventListener('click', this.toggleMenu.bind(this));
    this.controlsList?.querySelectorAll('button[data-action]').forEach((button) => {
      button.addEventListener('click', this.lightboxControls.bind(this));
    });
    this.updateActionButtons();
  }

  toggleMenu() {
    const isExpanded = this.controlToggle.getAttribute('aria-expanded') === 'true';
    if (this.controlsList) {
      this.controlsList.classList.toggle('actions-open', !isExpanded);
      this.controlsList.classList.toggle('actions-close', isExpanded);
    }

    const splideArrows = this.mediaGallery.querySelectorAll('[data-nav]');
    if (splideArrows.length) {
      splideArrows.forEach((arrow) => {
        arrow.classList.toggle('hidden', !isExpanded);
      });
    }

    if (this.menuIcon && this.closeIcon) {
      this.menuIcon.classList.toggle('hidden', !isExpanded);
      this.closeIcon.classList.toggle('hidden', isExpanded);
    }
    this.controlToggle.setAttribute('aria-expanded', String(!isExpanded));

    // Reset transforms when closing controls
    if (isExpanded) this.resetTransform();
  }

  lightboxControls(event) {
    const action = event.currentTarget.dataset.action;
    const activeMediaItem = this.mediaGallery?.querySelector('[data-media-item].swiper-slide-active img');

    if (!activeMediaItem) return;

    switch (action) {
      case 'move-right':
        this.transformValues.x = Math.min(this.transformValues.x + 10, 50);
        break;
      case 'move-left':
        this.transformValues.x = Math.max(this.transformValues.x - 10, -50);
        break;
      case 'move-down':
        this.transformValues.y = Math.min(this.transformValues.y + 10, 100);
        break;
      case 'move-up':
        this.transformValues.y = Math.max(this.transformValues.y - 10, -100);
        break;
      case 'zoom-in':
        this.transformValues.scale = Math.min(this.transformValues.scale + 0.1, 2);
        break;
      case 'zoom-out':
        this.transformValues.scale = Math.max(this.transformValues.scale - 0.1, 1);
        break;
      default:
        return;
    }

    this.applyTransform(activeMediaItem);
    this.updateActionButtons();
  }

  applyTransform(media) {
    const { x, y, scale } = this.transformValues;
    media.style.transition = 'transform 0.3s ease-in-out';
    media.style.transform = `translate(${x}%, ${y}%) scale(${scale})`;
  }

  updateActionButtons() {
    const isZoomedIn = this.transformValues.scale > 1;
    this.moveButtons.forEach((button) => {
      button.disabled = !isZoomedIn;
    });
    if (this.zoomOutButton) {
      this.zoomOutButton.disabled = this.transformValues.scale === 1;
    }
  }

  resetTransform() {
    const activeMediaItem = this.mediaGallery?.querySelector('[data-media-item].swiper-slide-active img');
    if (activeMediaItem) {
      this.transformValues = { x: 0, y: 0, scale: 1 };
      this.applyTransform(activeMediaItem);
      this.updateActionButtons();
    }
  }
}

customElements.define('media-lightbox-viewer', MediaLightBoxViewerModal);

const commonDefaults = {
  spread: 360,
  ticks: 50,
  gravity: 0,
  decay: 0.94,
  startVelocity: 30,
};
class Burstefects extends HTMLElement {
  constructor() {
    super();
    const { triggerPosition, effect, triggerOnce, duration } = this.getDefaultAttributes();

    let rootMargin = '0px';
    this.addEventListener;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const rect = entry.target.getBoundingClientRect();

            let triggerPositionState = this.getTriggerPositionState(triggerPosition, rect);
            if (triggerPositionState !== 'none') {
              this.triggerEffect(effect, duration);
            }

            if (triggerPositionState !== 'none') {
              if (triggerOnce === 'true') {
                observer.disconnect();

                console.log('Observer disconnected after first trigger.');
                return;
              }
            }
          }
        });
      },
      {
        root: null,
        rootMargin,
        threshold: [0, 0.5, 1],
      },
    );

    this.observer.observe(this);
  }

  // Disconnect the observer when the element is removed from the DOM
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Get default attribute values
  getDefaultAttributes() {
    const triggerPosition = this.dataset.triggerPosition || 'center'; // default to 'center'
    const effect = this.dataset.effect || 'cannon'; // default to 'cannon'
    const triggerOnce = this.dataset.triggerOnce;
    const duration = this.dataset.duartion || 1000; // default to 1000ms if not provided
    return { triggerPosition, effect, triggerOnce, duration };
  }

  // Determine the trigger position based on element's position
  getTriggerPositionState(triggerPosition, rect) {
    if (triggerPosition === 'top' && rect.top <= 0) {
      return 'top';
    } else if (
      triggerPosition === 'center' &&
      rect.top + rect.height / 2 <= window.innerHeight / 2 &&
      rect.bottom >= window.innerHeight / 2
    ) {
      return 'center';
    } else if (triggerPosition === 'bottom' && rect.bottom >= window.innerHeight) {
      return 'bottom';
    }
  }

  // Trigger the appropriate effect based on the specified effect type
  triggerEffect(effect = 'cannon', duration) {
    switch (effect) {
      case 'falling-ribbons':
        fallingRibbons(duration);
        break;
      case 'firework':
        firework(duration);
        break;
      case 'school-pride':
        sideConfetti(duration);
        break;
      case 'star':
        stars(duration);
        break;
      case 'valentine':
        valentine(duration);
        break;
      default:
        cannonConfetti(duration);
        break;
    }
  }
}
customElements.define('burst-efects', Burstefects);

function cannonConfetti(duration = 1000) {
  const animationEnd = Date.now() + duration;
  let skew = 1;

  function randomColor() {
    const colors = ['#ffffff', '#e9fa01', '#f93963', '#0b55e8', '#7c0e8f'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  (function frame() {
    const timeLeft = animationEnd - Date.now();
    const ticks = Math.max(200, 500 * (timeLeft / duration));
    skew = Math.max(0.8, skew - 0.001);
    const defaults = {
      particleCount: 1,
      startVelocity: 0,
      ticks,
      colors: [randomColor()],
      zIndex: 999,
    };
    (confetti({
      ...defaults,
      origin: {
        x: Math.random(),
        y: Math.random() * skew - 0.2,
      },
      gravity: randomInRange(0.2, 0.4),
    }),
      confetti({
        ...defaults,
        origin: {
          x: Math.random(),
          y: 0,
        },
        gravity: randomInRange(0.4, 0.6),
        scalar: randomInRange(1.5, 2),
        drift: randomInRange(-0.4, 0.4),
      }),
      timeLeft > 0 && requestAnimationFrame(frame));
  })();
}

function fallingRibbons(duration = 1000) {
  const heart = confetti.shapeFromPath({
      path: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
    }),
    star = confetti.shapeFromPath({
      path: 'M43.767 59.6698L31.4327 53.1854L19.094 59.6698C14.3183 62.187 8.68819 58.1198 9.60225 52.7741L11.9573 39.0363L1.97805 29.3081C-1.89655 25.5351 0.242508 18.9273 5.60265 18.1492L19.396 16.1429L25.5655 3.64453C27.971 -1.22187 34.901 -1.20781 37.2995 3.64453L43.467 16.1429L57.2604 18.1492C62.6135 18.9265 64.7643 25.5308 60.885 29.3081L50.9057 39.0363L53.2608 52.7741C54.1737 58.1143 48.5592 62.1831 43.767 59.6698Z',
    }),
    shortRibbon = confetti.shapeFromPath({
      path: 'M13.4189 115.023L5.75487 107.359C-1.91777 99.6915 -1.91777 87.277 5.75487 79.6048L13.4189 71.9408C15.9681 69.3892 15.9681 65.2392 13.4166 62.6876L5.75448 55.0236C-1.91816 47.3556 -1.91816 34.9412 5.75448 27.269L16.0021 17.0213C17.9951 15.026 18.4873 11.9913 17.2267 9.46783C15.6127 6.23737 16.9224 2.30808 20.1548 0.692064C23.3787 -0.922386 27.3162 0.38269 28.9302 3.61979C32.7122 11.1862 31.2345 20.2901 25.2521 26.2717L15.0056 36.5193C12.4435 39.0861 12.4525 43.217 15.0056 45.7701L22.6697 53.4365C30.3212 61.0904 30.3212 73.5419 22.6697 81.1911L15.0056 88.8552C12.4435 91.422 12.4525 95.5528 15.0056 98.1059L22.6697 105.772C25.2251 108.325 25.2251 112.47 22.6697 115.023C20.1138 117.579 15.974 117.579 13.4189 115.023Z',
    }),
    longRibbon = confetti.shapeFromPath({
      path: 'M15.0039 141.17C13.739 142.435 13.0843 144.049 13.0843 145.793C13.0843 147.538 13.7386 149.195 15.0039 150.416L15.4402 150.896L22.6363 158.092C30.3117 165.768 30.3117 178.198 22.6363 185.874C21.3714 187.139 19.714 187.793 18.0136 187.793C17.141 187.793 16.2687 187.618 15.4398 187.226C14.6988 186.921 14 186.485 13.3898 185.874C10.8597 183.301 10.8597 179.158 13.3898 176.584C15.9629 174.055 15.9629 169.911 13.3898 167.338L5.75741 159.706C2.05 155.999 0 151.07 0 145.793C0 140.559 2.05 135.631 5.75741 131.924L13.3898 124.248C15.9629 121.718 15.9629 117.575 13.3898 115.002L5.75741 107.369C2.05 103.662 0 98.7338 0 93.4565C0 88.2229 2.05 83.2944 5.75741 79.5873L13.3898 71.9112C15.9629 69.3815 15.9629 65.2382 13.3898 62.6651L5.75741 55.0327C2.05 51.3257 0 46.3972 0 41.1198C0 35.8863 2.05 30.9577 5.75741 27.2507L15.4402 17.5695L15.9636 17.0027C17.9703 14.9965 18.4492 11.9871 17.2281 9.4574C15.6148 6.23006 16.9238 2.30468 20.1511 0.691013C23.3785 -0.922656 27.3035 0.386326 28.9175 3.61327C32.7117 11.1586 31.2285 20.2738 25.2535 26.2488L15.0043 36.498C13.7394 37.7628 13.0847 39.3765 13.0847 41.121C13.0847 42.8655 13.739 44.523 15.0043 45.744L15.4406 46.2237L22.6367 53.4198C23.073 53.8561 23.5527 54.3358 23.9882 54.9026C30.2691 62.6221 29.8328 74.0049 22.6367 81.2018L15.0043 88.8342C13.7394 90.099 13.0847 91.7127 13.0847 93.4572C13.0847 95.2018 13.739 96.8592 15.0043 98.0803L15.4406 98.56L22.6367 105.756C30.312 113.432 30.312 125.862 22.6367 133.538L15.4406 140.734L15.0039 141.17Z',
    }),
    animationEnd = Date.now() + duration;

  let skew = 1;
  function randomColor() {
    const colors = ['#ffffff', '#e9fa01', '#f93963', '#0b55e8', '#7c0e8f'];
    return colors[Math.floor(Math.random() * colors.length)];
  }
  (function frame() {
    const timeLeft = animationEnd - Date.now(),
      ticks = Math.max(200, 500 * (timeLeft / duration));
    skew = Math.max(0.8, skew - 0.001);
    const defaults = {
      particleCount: 1,
      startVelocity: 0,
      ticks,
      colors: [randomColor()],
      zIndex: 999,
    };
    (confetti({
      ...defaults,
      origin: {
        x: Math.random(),
        y: Math.random() * skew - 0.2,
      },
      gravity: randomInRange(0.2, 0.4),
    }),
      confetti({
        ...defaults,
        origin: {
          x: Math.random(),
          y: 0,
        },
        shapes: [shortRibbon, longRibbon, heart, star],
        gravity: randomInRange(0.4, 0.6),
        scalar: randomInRange(1.5, 2),
        drift: randomInRange(-0.4, 0.4),
      }),
      timeLeft > 0 && requestAnimationFrame(frame));
  })();
}

function firework(duration = 1000) {
  const animationEnd = Date.now() + duration;
  const interval = setInterval(function () {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 20 * (timeLeft / duration);

    // Use common defaults and extend them
    confetti({
      ...commonDefaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
    });
    confetti({
      ...commonDefaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
    });
  }, 50);
}

function stars(duration = 1000) {
  const starDefaults = {
    ...commonDefaults,
    shapes: ['star'],
    colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'],
  };

  function shoot() {
    confetti({
      ...starDefaults,
      particleCount: 40,
      scalar: 1.2,
    });

    confetti({
      ...starDefaults,
      particleCount: 10,
      scalar: 0.75,
      shapes: ['circle'],
    });
  }

  setTimeout(shoot, 0);
  setTimeout(shoot, 100);
  setTimeout(shoot, 200);
}

function valentine(duration = 1000) {
  const heart = confetti.shapeFromPath({
    path: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
    matrix: [0.03333333333333333, 0, 0, 0.03333333333333333, -5.566666666666666, -5.533333333333333],
  });
  const valentineDefaults = {
    ...commonDefaults,
    shapes: [heart],
    colors: ['FFC0CB', 'FF69B4', 'FF1493', 'C71585'],
  };

  confetti({
    ...valentineDefaults,
    particleCount: 50,
    scalar: 2,
  });

  confetti({
    ...valentineDefaults,
    particleCount: 25,
    scalar: 3,
  });

  confetti({
    ...valentineDefaults,
    particleCount: 10,
    scalar: 4,
  });
}

function sideConfetti(duration = 1000) {
  const star = confetti.shapeFromPath({
      path: 'M43.767 59.6698L31.4327 53.1854L19.094 59.6698C14.3183 62.187 8.68819 58.1198 9.60225 52.7741L11.9573 39.0363L1.97805 29.3081C-1.89655 25.5351 0.242508 18.9273 5.60265 18.1492L19.396 16.1429L25.5655 3.64453C27.971 -1.22187 34.901 -1.20781 37.2995 3.64453L43.467 16.1429L57.2604 18.1492C62.6135 18.9265 64.7643 25.5308 60.885 29.3081L50.9057 39.0363L53.2608 52.7741C54.1737 58.1143 48.5592 62.1831 43.767 59.6698Z',
      matrix: [0.03597122302158273, 0, 0, 0.03597122302158273, -4.856115107913669, -5.071942446043165],
    }),
    heart = confetti.shapeFromPath({
      path: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
      matrix: [0.03333333333333333, 0, 0, 0.03333333333333333, -5.566666666666666, -5.533333333333333],
    }),
    end = Date.now() + duration,
    defaults = {
      particleCount: 3,
      startVelocity: 80,
      spread: 33,
      colors: ['#ffff00', '#bb0000', '#ffffff'],
    };
  (function frame() {
    (confetti({
      ...defaults,
      angle: 60,
      origin: {
        x: 0,
      },
    }),
      confetti({
        ...defaults,
        angle: 60,
        origin: {
          x: 0,
        },
        shapes: [star, heart],
        scalar: randomInRange(1, 1.5),
      }),
      confetti({
        ...defaults,
        angle: 120,
        origin: {
          x: 1,
        },
      }),
      confetti({
        ...defaults,
        angle: 120,
        origin: {
          x: 1,
        },
        shapes: [star, heart],
        scalar: randomInRange(1, 1.5),
      }),
      Date.now() < end && requestAnimationFrame(frame));
  })();
}
// Helper function for generating random numbers within a range
function randomInRange(min, max) {
  return Math.random() * (max - min) + min;
}

function getSectionsBelowElementById(startId) {
  const startElement = document.getElementById(startId);
  if (!startElement) return [];
  const allSections = Array.from(document.querySelectorAll('section'));
  const startIndex = allSections.indexOf(startElement);
  if (startIndex === -1) return [];
  return allSections.slice(startIndex + 1);
}

function refreshScrollTriggersForSubsequentSections(element, timePerSection = 50, minScreenWidth = 768) {
  let totalTime = timePerSection;
  let counterSec = 0;

  const sectionDataId = element.closest('section')?.getAttribute('id');
  if (!sectionDataId || typeof ScrollTrigger === 'undefined') return;

  const sectionBelowData = getSectionsBelowElementById(sectionDataId);
  if (!sectionBelowData.length) return;

  sectionBelowData.forEach((eleSection) => {
    const animatedSection = eleSection.querySelector('[mayaathemesection]');
    if (animatedSection) {
      const dataSectionId = animatedSection.getAttribute('data-section');
      const triggerScroll = ScrollTrigger.getById(dataSectionId);
      if (triggerScroll) counterSec++;
    }
  });

  if (counterSec > 0 && window.innerWidth >= minScreenWidth) {
    const scrollSections = document.querySelectorAll('[mayaathemesection]');
    const totalLength = scrollSections.length;
    totalTime = totalLength * timePerSection;

    setTimeout(() => ScrollTrigger.refresh(), totalTime);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  let resizeTimeout;
  const init = () => {
    if (window.innerWidth < 767) {
      const getAllMarquee = document.querySelectorAll('wavy-marquee');
      if (getAllMarquee) {
        getAllMarquee.forEach((marquee) => {
          let classObjectIntiate = new gsapMayaaThemeExecution();
          classObjectIntiate.wavyMarquee(marquee);
          const getId = marquee.dataset.section;
          if (getId) {
            const getScrollId = ScrollTrigger.getById(getId);
            if (getScrollId) {
              getScrollId.refresh();
            }
          }
        });
      }
    }
  };
  setTimeout(() => {
    init();
  }, 1000);
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      setTimeout(() => {
        init();
      }, 1000);
    });
  }
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      init();
    }, 1000); // adjust delay if needed
  });
});
